// The board's client state: the projects, issues, record entries and
// questions the server confirmed, with the person's own changes to issues on
// top while they are on their way (today's BoardStore, ported to the
// server's contract).
//
// - Confirmed rows live in `base` maps, as the server sent them. A change the
//   person makes to an issue is a patch in `pending`, shown at once over its
//   row and sent as its op with a fresh mutation id. The answer's
//   `{seq, result}` replaces the patch with the row the server confirms; a
//   refusal drops the patch, which rolls the change back, and the store says
//   so. A new issue is the same: its row, as the store expects the server to
//   make it, is a patch over nothing until the server answers.
// - A new project is not shown before the server confirms it, since its key
//   may be refused; once it is made, the board shows it.
// - Online, an answer is not shown before the server confirms it. Offline,
//   its durable outbox entry shows the question answered and pending. When the
//   answer is refused, the store reads the feed before it says why, so the
//   question shows as it now stands: at its new version, answered, or gone.
// - The store follows the change feed from its cursor: live, over a
//   WebSocket (`feed`, api/feed.ts), while one is open, and by polling while
//   it is not. Once the board is loaded it opens the socket from its cursor,
//   and stops polling while the socket is open. When the socket closes, it
//   polls again, and opens another from its cursor after a backoff that
//   grows while it keeps failing. The socket and the poll give back the
//   snapshot's epoch with the cursor, so a restored server, whose seqs may
//   have passed the cursor with other changes, answers 410 or 4410
//   (Poulpe-792). A close that says the server restarts
//   (1012) starts the backoff again, and keeps each wait within 5 s for 15
//   minutes, the longest planned upgrade (Poulpe-659). When the feed answers that it cannot
//   continue from the cursor (a 410 `snapshot_needed`, or the socket's
//   close 4410), the store takes a snapshot, and it takes one whenever it is
//   told to (`takeSnapshot`). Once the last snapshot on its way has ended,
//   kept or dropped, the store opens the socket from its cursor. A socket
//   that closes because the session ended (4401) is a close like the others,
//   and the store reads who is signed in as well (below).
// - Each row keeps the seq of the change it came from, so an older row (a
//   mutation's answer that trails the feed) never replaces a newer one.
// - It keeps the questions in play, as the snapshot does: open, or answered
//   and not yet confirmed delivered. A change that leaves one otherwise
//   removes it.
// - It knows who the pages act as (`/v1/me`), read before the first
//   snapshot, and names that member on every request and socket after it
//   (`as`, api/types.ts). The server refuses one whose session or token is
//   now someone else's, 401 `identity_changed`, and reads or changes
//   nothing: so no poll, socket or change made as one person ever reaches
//   another's board, whatever the store has yet to learn.
// - Any 401, and any socket closed by 4401, has the store read who is signed
//   in (`refreshSession`), and only that read decides: no one, and the store
//   forgets the board and says the person must sign in; someone else, and it
//   forgets the board and loads theirs; the same person, and nothing is
//   forgotten (drafts, picks and pending changes stay), and the feed goes on
//   from the cursor. A change the server answered 401 is not a refusal: it
//   stays in the outbox and on screen, neither rolled back nor reported (the
//   server took nothing yet), and is sent once that read finds the same
//   person; signed out, it waits on this device until the person signs in
//   again (Poulpe-405). Signing up or in (a passkey ceremony) loads the
//   board again; signing out forgets it.
//   Signing out ends the browser's session whoever holds it: it names no one.
//   Forgetting closes the socket, and drops whatever it, or a request made
//   before, sends after.
//   When a bearer token signs the pages in instead (`byToken`), there is
//   nothing to sign in or out of, and a 401 is an error like any other.
// - The session's cookie is every tab's: another tab that signs out, or in
//   as someone else, changes this tab's session without a word to it. So a
//   tab tells the others when it signs in or out (`sessions`, a
//   BroadcastChannel), and reads `/v1/me` again when told, or when asked
//   (`refreshSession`, as the tab comes to the front): when the person or the
//   workspace changed, it forgets the board and loads the new one.
// - Reads of who is signed in may overlap, and land in any order: only the
//   last one asked for (`sessionRead`), by `load` or `refreshSession`, may
//   change who the store acts as or sign it out, so that an older answer
//   never pairs a person with another's board, nor a late 401 strands it.
//   A 401 or a 4401 asks only when no read is on its way, and otherwise
//   waits for that one: polls that keep failing would replace each read
//   before it lands, were `/v1/me` slower than they are. When the read it
//   waited for finds the same person, though the session changed after it
//   was asked, the next 401 finds none on its way and asks again. What must
//   wait for who is signed in before it goes on, as a session's transcript
//   after a 401 of its own, waits for the last read asked for to decide
//   (`whenSessionKnown`), not for whichever lands first.
// - It keeps the agents' sessions, from the snapshot, the feed and the
//   answers to their ops (`sendSessionOp`), which the pages send with their
//   own mutation ids (sessions/). Their transcripts are not the store's.
// - It keeps the landing queue: the open proposals and the running landings,
//   from the snapshot and the feed. A proposal the feed shows ended, and a
//   landing it shows finished, leave. A person approves a reserved landing
//   with a fresh passkey confirmation of exactly its subject
//   (`approveLanding`), unless their account asks for none
//   (`confirms_actions`, Poulpe-686): then the approval goes first, and a
//   passkey is asked for only if the server still asks. A person says a
//   proposal looks good, or asks its session for changes
//   (`lookAtProposal`, `returnProposal`). A person merges ready proposals
//   together (`mergeTogether`); a batch that failed
//   holds its proposals, and stays, finished, while it holds one, to say
//   why, until the person dismisses it here (`dismissLanding`).
// - A person makes a runner's code (`makeRunnerCode`) with a fresh passkey
//   confirmation of exactly it too, and saves a landing policy so
//   (`setProjectPolicy`), unless their account asks for none, as for an
//   approval. What the server makes is shown once, and the store keeps
//   none of it.
// - A person signs in or up by email too (`startEmail`): the server mails a
//   link, whose verifier this browser keeps (people/emailLink.ts). Finishing
//   it (`finishEmailLink`), from the link's page in a browser or from the
//   Mac app's `poulpe-email-link` event (`emailLinks`), signs in as a
//   passkey's ceremony does, or, signed in already, takes the person's new
//   address. A link is finished once, whoever asks again; what it came to
//   shows on sign-in while signed out (`emailLink`). Adding an address or a
//   passkey (`addEmail`, `addPasskey`) asks first, and confirms with a
//   passkey only when the server says the session is not fresh enough.
//   Removing a passkey (`removePasskey`) always takes one: a confirmation of
//   exactly that passkey (`remove_passkey`), asked for when the server says
//   so; naming one (`namePasskey`) takes none. A person whose account asks
//   for no confirmation is asked for none of these. Turning confirmations
//   off (`setConfirmations`) takes one last confirmation of
//   `stop_confirmations`, asked for when the server says so; turning them
//   back on takes none.
// - It keeps the projects' labels that are not deleted, from the snapshot,
//   the feed and the answers to their ops (Poulpe-389). A label is not shown
//   before the server makes it, since its name may be refused. Edits and
//   deletions use the outbox. A label the feed shows deleted leaves, and
//   the issues it left come by the feed. A label put on an issue or taken off, a claim
//   taken or released, and a move are patches, as any change to an issue: a
//   move to another state without a place shows the issue at the top of the
//   new column, as the server puts it (sortKey.ts), and so does a new issue.
// - It keeps the plans that are not archived (Poulpe-410), from the snapshot,
//   the feed and the answers to their ops, without their bodies. A page that
//   shows a plan watches it (`watchPlan`): the store reads its body, its
//   revisions and its comments (`GET /v1/plans/{id}`), and reads them again
//   whenever the feed's `revision` passes the one it read. A plan's comments
//   come by the feed too, and join the body read. The person's changes to a
//   plan go through the outbox as those to an issue do: a new plan, its
//   title, summary and status, its body with the base it was edited from
//   (refused 409 `text_conflict` when it cannot merge, kept as a refused
//   change with both texts), an issue listed in it, its claim, and comments.
import { ApiError, errorMessage, isOffline } from '../api/client'
import { FEED_ABNORMAL, FEED_RESTART, FEED_SNAPSHOT_NEEDED, FEED_UNAUTHORIZED, type FeedSocket, type OpenFeed } from '../api/feed'
import { browserPasskeys, PasskeyError, type Passkeys } from '../api/passkeys'
import { EMAIL_LINK_REFUSED, LAST_PASSKEY, PASSKEY_REFUSED, SNAPSHOT_NEEDED, STALE, UNCONFIRMED } from '../api/types'
import type { ConfirmAction, CreationOptions, EmailStarted, PasskeyList, RunnerCode, SignUpStatus, Started } from '../api/types'
import { ADD_PASSKEY, addEmailAction, confirmsActions, removePasskeyAction, STOP_CONFIRMATIONS } from '../people/actions'
import { challengeOf, forgetVerifier, keepVerifier, keptVerifier, newVerifier } from '../people/emailLink'
import { emailWords } from '../people/words'
import { emailLinkApp } from '../mac/emailLinks'
import type { WorkDigest, Api, ApprovalAsked, AttachFileArgs, BoardAttachment, Change, Entity, EntryKind, Issue, IssueRevision, IssueRevisions, Label, Landing, Me, MoveIssueArgs, Mutation, MutationResult, Op, Part, PlanComment, PlanDetail, PlanPatchOp, PlanRow, PmAgent, LandingPolicy, ReleaseMap, Project, Proposal, Question, RecordEntry, RelationKind, Reply, ReviewOutcome, Run, Runner, RunnerJob, SearchAnswer, SearchOptions, Session, SessionOp, SetProjectColumnsArgs, StateSlug, UnsentPrompt, UpdateIssueArgs, UpdateLabelArgs, UpdatePlanArgs, Urgency, WorkerLimits } from '../api/types'
import { policyAction } from '../settings/policy'
import { subjectDigest } from '../queue/subject'
import { REGISTER_RUNNER } from '../runners/registration'
import { uuid4, uuid7 } from '../api/uuid'
import type { LocalWriteOutcome } from '../api/shellLocalReads'
import { firstKey, topKey } from '../sortKey'
import { formatOf } from '../plans/format'
import { sortRelations } from '../issue/relations'
import { issueKey, PART_NAMES, PARTS } from '../vocab'
import { lastIdentity, openLocal, rememberIdentity, type LocalBase, type LocalStore, type OutboxEntry, type PendingPrompt, type RefusedChange } from './local'

/** Where the tabs of this browser tell each other that its session changed: a BroadcastChannel. */
export interface SessionChannel {
  postMessage(message: string): void
  addEventListener(type: 'message', listener: () => void): void
  removeEventListener(type: 'message', listener: () => void): void
}

/** `signed_out`: there is no session, and the person must sign in. */
export type LoadStatus = 'loading' | 'ready' | 'error' | 'signed_out'

export interface BoardState {
  offline: boolean
  waiting: number
  refused: readonly RefusedChange[]
  status: LoadStatus
  error: ApiError | null
  /** Who the pages act as, and in which workspace; null until it is read, and when signed out. */
  me: Me | null
  /** In the server's order. */
  projects: ReadonlyMap<string, Project>
  /** The projects' labels that are not deleted, by id. */
  labels: ReadonlyMap<string, Label>
  /** The project the board shows: the first, unless one was chosen. */
  selectedProjectId: string | null
  /** With the pending changes applied. */
  issues: ReadonlyMap<string, Issue>
  /** Every entry of every record, by id. */
  entries: ReadonlyMap<string, RecordEntry>
  /** The questions in play, by id. */
  questions: ReadonlyMap<string, Question>
  /**
   * The last row of each question that left play while this page ran
   * (answered and delivered, or cancelled), by id: in memory alone, and
   * forgotten with the board. It tells a screen that showed one why it went.
   */
  settled: ReadonlyMap<string, Question>
  pendingAnswers: ReadonlySet<string>
  /** The issues whose claim by the person waits to be sent or confirmed: provisional until the feed confirms it (Poulpe-826). */
  provisionalClaims: ReadonlySet<string>
  /** The agents' sessions, by id. */
  sessions: ReadonlyMap<string, Session>
  /** Attachment metadata; bytes are fetched on demand. */
  attachments: ReadonlyMap<string, BoardAttachment>
  /** The runs the board keeps (the active ones and the newest others), by id. */
  runs: ReadonlyMap<string, Run>
  /** The workspace's runners, revoked ones included, by id, live from the feed. */
  runners: ReadonlyMap<string, Runner>
  /** The runners' jobs with no outcome yet, by id. */
  jobs: ReadonlyMap<string, RunnerJob>
  /** The open proposals, by id. */
  proposals: ReadonlyMap<string, Proposal>
  /** The proposals returned to their session that are their issue's newest, by id: Stuck reads them. */
  returned: ReadonlyMap<string, Proposal>
  /** The running landings, and the batches waiting to run, by id. */
  landings: ReadonlyMap<string, Landing>
  /** The finished landings that hold an open proposal (`held_by`), by id: they say why it is held. */
  held: ReadonlyMap<string, Landing>
  /** The held landings the person dismissed on this page, by id. */
  dismissed: ReadonlySet<string>
  /** The live feed: open; closed and being opened again, while the board is polled; or none followed. */
  stream: 'live' | 'reconnecting' | 'none'
  /** While signed out, an email link being finished, or why it did not sign in; null otherwise. */
  emailLink: EmailLinkState | null
  /** The plans not archived, by id, with the pending changes applied; without their bodies. */
  plans: ReadonlyMap<string, PlanRow>
  /** The plans read for a page that watches them (`watchPlan`), with their bodies and comments, pending changes applied. */
  planDetails: ReadonlyMap<string, PlanDetail>
  /** Why a watched plan could not be read: a 404 when there is no such plan. */
  planErrors: ReadonlyMap<string, ApiError>
}

export interface BoardStoreOptions {
  api: Api
  /** The Mac shell is told only accepted authentication, sign-out or a conclusive /me refusal. */
  onSessionState?: (state: 'confirmed' | 'authenticated' | 'invalidated') => void
  /** The Mac shell restores offline identity only while a saved credential exists. Browsers default to true. */
  offlineSessionAllowed?: boolean
  /** Server origin used to isolate local data. Defaults to the page's origin. */
  origin?: string
  /** Disable disk storage for isolated, in-memory fixtures. */
  local?: boolean
  /** The browser's passkeys, for signing up and in; the tests and the mock fake them. */
  passkeys?: Passkeys
  /** A bearer token signs the pages in (`POULPE_TOKEN_FILE`, `poulpe.token`), not a session. */
  byToken?: boolean
  /** Where the Mac app's shell hands over an email link, as a `poulpe-email-link` event; none heard by default. */
  emailLinks?: Pick<EventTarget, 'addEventListener' | 'removeEventListener'> | null
  /** The Mac shell may deliver queued links only while this listener is installed. */
  onEmailLinksReady?: (ready: boolean) => void
  /** The other tabs, told when this one signs in or out, and telling it; none in the token mode. */
  sessions?: SessionChannel | null
  /** How often the feed is read while no socket brings it; 0 reads it only when asked (`catchUp`). */
  pollMs?: number
  /** Opens the live feed; none, and the store polls alone. */
  feed?: OpenFeed | null
  /** How long to wait before opening the socket again, after `attempt` failures in a row (0 for the first). */
  reconnectMs?: (attempt: number) => number
  /** Changes asked for at once. */
  pageSize?: number
  /** Told, in words, of each change to an issue that was refused and rolled back. */
  onFailure?: (message: string) => void
  newMutationId?: () => string
  /** The id of a project or an issue the person makes: a UUID v7, as the server's. */
  newId?: () => string
}

/** What a change the person makes to an issue shows at once: the fields it sets. */
export type IssuePatch = Partial<Pick<Issue, 'title' | 'priority' | 'description' | 'state' | 'column' | 'plan' | 'outcome' | 'labels' | 'claim' | 'sort_key' | 'relations' | 'parent'>>

/**
 * What an outbox entry shows on its issue: its patch, but for a relation's
 * change, which is replayed on the issue as it now stands. A patch holds the
 * whole list, so a refused change before it would otherwise stay shown.
 */
function applyIssuePatch(issue: Issue, { patch, mutation }: Pick<OutboxEntry, 'patch' | 'mutation'>, issues: ReadonlyMap<string, Issue>): Issue {
  const next = { ...issue, ...patch }
  if (mutation.op !== 'relate_issues' && mutation.op !== 'unrelate_issues') return next
  const { kind, other } = mutation.args
  const relations = (issue.relations ?? []).filter((r) => !(r.kind === kind && r.issue === other))
  if (mutation.op === 'relate_issues') {
    const key = issues.get(other)?.key ?? patch.relations?.find((r) => r.issue === other)?.key
    if (key) relations.push({ kind, issue: other, key })
  }
  return { ...next, relations: sortRelations(relations) }
}

/** The fields `update_issue` changes. */
export type IssueFields = Pick<UpdateIssueArgs, 'title' | 'priority' | 'description' | 'summary'>

type Pending = Pick<OutboxEntry, 'mutation' | 'mutationId' | 'issueId' | 'patch' | 'created' | 'record' | 'answered' | 'planId' | 'planPatch' | 'createdPlan' | 'planBody' | 'comment' | 'sessionId' | 'sessionTitle' | 'label'>

/** What a change the person makes to a plan shows at once: the fields it sets. */
export type PlanPatch = Partial<Pick<PlanRow, 'title' | 'summary' | 'status' | 'claim' | 'issues' | 'archived_at'>>

/** How making a project fared: made, and shown on the board, or refused with the server's words. */
export type ProjectOutcome = { ok: true } | { ok: false; message: string }

/** How a change to a project's labels fared: made, with the label as the server answered it, or refused with the server's words. */
export type LabelOutcome = { ok: true; label: Label } | { ok: false; message: string }

/** How an answer fared: taken, or refused with the server's words and whether the question changed since it was read. */
/** How an answer fared: taken, or not, with the server's words and whether the question changed; `offline` when the caller keeps it (a `mutationId` given) and the store sent nothing. */
export type AnswerOutcome = { ok: true } | { ok: false; stale: boolean; message: string; offline?: true }

/**
 * Why signing up or in failed: the server refused the passkey, the person
 * closed the browser's prompt, the browser has no passkeys, or something
 * else, which `message` says.
 */
export type SignInProblem = 'refused' | 'cancelled' | 'unsupported' | 'failed'

/** How signing up or in fared. Once it is ok, the board loads. */
export type SignInOutcome = { ok: true } | { ok: false; problem: SignInProblem; message: string }

/** Why an action the person confirms with a passkey, such as a runner's code, was not taken: as for an approval, but never a mismatch. */
export type ConfirmedProblem = Exclude<ApprovalProblem, 'mismatch'>

/** How an action the person confirms with a passkey fared: what the server made, shown this once, or why not. */
export type ConfirmedOutcome<T> = { ok: true; made: T } | { ok: false; problem: ConfirmedProblem; message: string }

/**
 * Why an approval was not sent, or not taken: the subject shown is not the
 * one the server asks for (`mismatch`, and nothing was sent), the person
 * the page acts as changed on the way (`identity_changed`, and nothing more
 * was sent), the server
 * refused the passkey, the person closed the prompt, the browser has no
 * passkeys, or something else, which `message` says.
 */
export type ApprovalProblem = 'mismatch' | 'identity_changed' | 'refused' | 'cancelled' | 'unsupported' | 'failed'

/**
 * How an approval fared: sent, and accepted by the landing or not (it
 * approved another subject than the one now asked, which `landing` shows),
 * or not sent, or refused.
 */
export type ApprovalOutcome = { ok: true; accepted: boolean; landing: Landing } | { ok: false; problem: ApprovalProblem; message: string }

/** How a Merge together fared: the landing it made, or the server's refusal, with its status and reason for the page's words. */
export type MergeOutcome = { ok: true; landing: Landing } | { ok: false; status: number; reason: string | undefined; message: string }

/** How asking for an email link fared: on its way, said alike whatever the address's account, or not asked, with what to say. */
export type EmailOutcome = { ok: true } | { ok: false; message: string }

/**
 * Why an email link did not finish: no verifier for it here (`elsewhere`:
 * asked for in another browser, or expired), the server refused it, or
 * something else, which `message` says.
 */
export type EmailLinkProblem = 'elsewhere' | 'refused' | 'failed'

/** How finishing an email link fared: signed in, with the page the person asked from, or why not. */
export type EmailLinkOutcome = { ok: true; back: string | null } | { ok: false; problem: EmailLinkProblem; message: string }

/** An email link while signed out: being finished, or why it did not sign in. */
export type EmailLinkState = { phase: 'finishing' } | { phase: 'failed'; problem: EmailLinkProblem; message: string }

/** As a confirmed action's outcome; or, `not_fresh`, there is no passkey to confirm with, and the session is too old to go without. */
export type AccountOutcome<T> = ConfirmedOutcome<T> | { ok: false; problem: 'not_fresh'; message: string }

/** As an account action's outcome; or, `last_passkey`, it was the person's last passkey and they have no address to sign in with instead. */
export type PasskeyRemoval = AccountOutcome<PasskeyList> | { ok: false; problem: 'last_passkey'; message: string }

/** What to say of an email link that did not finish. */
export function emailLinkProblemText(outcome: Extract<EmailLinkOutcome, { ok: false }>): string {
  switch (outcome.problem) {
    case 'elsewhere':
      return emailWords.link.elsewhere
    case 'refused':
      return emailWords.link.refused
    case 'failed':
      return `Could not sign in: ${outcome.message}`
  }
}

/** A socket open this long has worked: the backoff starts again after it closes. */
export const FEED_STABLE_MS = 10_000

/** After the server says it restarts, no wait is longer than this… */
export const RESTART_RETRY_MS = 5_000
/** …for this long: the longest planned upgrade (docs/hosted-service.md, D6). */
export const RESTART_WINDOW_MS = 15 * 60_000

/** Doubling from 1 s up to 30 s, each wait between half and all of that, so that tabs do not all come back at once. */
export function reconnectBackoff(attempt: number): number {
  return Math.min(30_000, 1_000 * 2 ** Math.min(attempt, 5)) * (0.5 + Math.random() / 2)
}

const EMPTY: BoardState = {
  offline: false,
  waiting: 0,
  refused: [],
  status: 'loading',
  error: null,
  me: null,
  projects: new Map(),
  labels: new Map(),
  selectedProjectId: null,
  issues: new Map(),
  entries: new Map(),
  questions: new Map(),
  settled: new Map(),
  pendingAnswers: new Set(),
  provisionalClaims: new Set(),
  sessions: new Map(),
  attachments: new Map(),
  runs: new Map(),
  runners: new Map(),
  jobs: new Map(),
  proposals: new Map(),
  returned: new Map(),
  landings: new Map(),
  held: new Map(),
  dismissed: new Set(),
  stream: 'none',
  emailLink: null,
  plans: new Map(),
  planDetails: new Map(),
  planErrors: new Map(),
}

/** What to say of a refused change: today's words for the same failures. */
export function failureMessage(key: string, patch: IssuePatch, err: unknown): string {
  const part = PARTS.find((p) => patch[p] !== undefined)
  const action =
    patch.state !== undefined ? `Could not change the state of ${key}`
    : patch.sort_key !== undefined ? `Could not move ${key}`
    : patch.priority !== undefined ? `Could not change the priority of ${key}`
    : patch.title !== undefined ? `Could not rename ${key}`
    : patch.labels !== undefined ? `Could not change the labels of ${key}`
    : patch.relations !== undefined ? `Could not change the relations of ${key}`
    : patch.parent !== undefined ? `Could not change the parent of ${key}`
    : patch.claim === null ? `Could not release ${key}`
    : patch.claim !== undefined ? `Could not claim ${key}`
    : part ? `Could not save the ${PART_NAMES[part].toLowerCase()} of ${key}`
    : `Could not change ${key}`
  return `${action}: ${errorMessage(err)}`
}

/** How many questions that left play the store keeps (`BoardState.settled`). */
const SETTLED_KEPT = 200

/** A question is in play while it is open, or answered and not yet confirmed delivered. */
export function inPlay(question: Question): boolean {
  return question.state === 'open' || (question.state === 'answered' && question.delivery !== 'confirmed')
}

/** A job is open until its runner has finished or refused it. */
export function jobOpen(job: RunnerJob): boolean {
  return job.state !== 'finished' && job.state !== 'refused'
}

/** What went wrong in a passkey ceremony. */
function signInProblem(err: unknown): SignInOutcome {
  const message = errorMessage(err)
  if (err instanceof PasskeyError) return { ok: false, problem: err.problem, message }
  if (err instanceof ApiError && err.reason === PASSKEY_REFUSED) return { ok: false, problem: 'refused', message }
  return { ok: false, problem: 'failed', message }
}

/** What to tell the person when asking for a link failed: the server's refusal is of the address, as the page checked the names. */
function emailStartProblem(err: unknown, doing: string): string {
  if (err instanceof ApiError && err.reason === EMAIL_LINK_REFUSED) return emailWords.notAnAddress
  return `Could not ${doing}: ${errorMessage(err)}`
}

/** What went wrong in a confirmation, or in the action it confirmed; a change of identity is the caller's to tell. */
function confirmationProblem(err: unknown): { ok: false; problem: Exclude<ConfirmedProblem, 'identity_changed'>; message: string } {
  const message = errorMessage(err)
  if (err instanceof PasskeyError) return { ok: false, problem: err.problem, message }
  if (err instanceof ApiError && err.reason === PASSKEY_REFUSED) return { ok: false, problem: 'refused', message }
  return { ok: false, problem: 'failed', message }
}

/** Whether two answers of `/v1/me` are the same member of the same workspace. */
function sameIdentity(a: Me, b: Me | null): boolean {
  return b !== null && a.workspace.id === b.workspace.id && a.member?.id === b.member?.id
}

/** The entity a mutation's result names. */
function entityOf(result: MutationResult['result']): Entity | null {
  if ('attachment' in result) return { entity: 'attachment', data: result.attachment }
  if ('label' in result) return { entity: 'label', data: result.label }
  if ('issue' in result) return { entity: 'issue', data: result.issue }
  if ('question' in result) return { entity: 'question', data: result.question }
  if ('record_entry' in result) return { entity: 'record_entry', data: result.record_entry }
  if ('project' in result) return { entity: 'project', data: result.project }
  if ('session' in result) return { entity: 'session', data: result.session }
  if ('proposal' in result) return { entity: 'proposal', data: result.proposal }
  if ('landing' in result) return { entity: 'landing', data: result.landing }
  if ('plan' in result) return { entity: 'plan', data: result.plan }
  if ('plan_comment' in result) return { entity: 'plan_comment', data: result.plan_comment }
  return null
}

export class BoardStore {
  private state: BoardState = EMPTY
  private readonly listeners = new Set<() => void>()

  private projectBase = new Map<string, Project>()
  private labelBase = new Map<string, Label>()
  private issueBase = new Map<string, Issue>()
  private entryBase = new Map<string, RecordEntry>()
  private questionBase = new Map<string, Question>()
  /** The questions that left play while this page ran (`BoardState.settled`), oldest first. */
  private settledBase = new Map<string, Question>()
  private sessionBase = new Map<string, Session>()
  private attachmentBase = new Map<string, BoardAttachment>()
  private runBase = new Map<string, Run>()
  private runnerBase = new Map<string, Runner>()
  private jobBase = new Map<string, RunnerJob>()
  private proposalBase = new Map<string, Proposal>()
  /** The proposals returned to their session that are their issue's newest: what Stuck reads. */
  private returnedBase = new Map<string, Proposal>()
  private landingBase = new Map<string, Landing>()
  /** The finished landings that did not land: kept while an open proposal is held by one. */
  private heldBase = new Map<string, Landing>()
  private planBase = new Map<string, PlanRow>()
  /** The plans read, with their bodies: of the pages that watch them. */
  private detailBase = new Map<string, PlanDetail>()
  private detailErrors = new Map<string, ApiError>()
  /** How many pages watch each plan. */
  private readonly watched = new Map<string, number>()
  /** The reads of a plan on their way, by plan; one at a time, and one more when asked meanwhile. */
  private readonly planReads = new Map<string, { again: boolean; comments: Map<string, PlanComment> }>()
  private pending: Pending[] = []
  private outbox: OutboxEntry[] = []
  private local: LocalStore | null = null
  private draining: Promise<void> | null = null
  private sendingId: string | null = null
  private retryTimer: ReturnType<typeof setTimeout> | null = null
  private persistTimer: ReturnType<typeof setTimeout> | null = null
  private readonly durable = new Map<string, Promise<void>>()
  private readonly outcomes = new Map<string, (ok: boolean) => void>()
  private readonly failures = new Map<string, (err: unknown) => string>()
  private readonly confirmations = new Map<string, Promise<void>>()
  private readonly origin: string
  private readonly useLocal: boolean
  /** The device keeps nothing (IndexedDB would not open): changes are sent from memory, as before. */
  private localUnavailable = false
  /** The seq of the change each row came from; a row with none is the snapshot's. */
  private readonly rowSeq = new Map<string, number>()
  private snapshotSeq = 0
  /** The feed's cursor: every change up to it has been applied. */
  private cursor = 0
  /** The epoch of the snapshot the cursor follows, given back with it; null when the server sent none (Poulpe-792). */
  private feedEpoch: string | null = null
  /** Bumped by each snapshot, so an answer asked for before it is dropped. */
  private generation = 0
  /** Bumped when the store forgets the board, so that no answer from before reaches what it shows next. */
  private epoch = 0
  /** Bumped by each read of who is signed in: only the answer to the last one asked for may change who the store acts as. */
  private sessionRead = 0
  /** The newest read of who is signed in whose answer came; below `sessionRead` while the last one asked for is on its way. */
  private sessionLanded = 0
  /** The newest read of who is signed in whose outcome the store has taken: below `sessionRead` until the last one asked for has decided. */
  private sessionDecidedRead = 0
  /** Who waits for the last read asked for to decide (`whenSessionKnown`). */
  private sessionWaiters: (() => void)[] = []
  private catching: Promise<void> | null = null
  private timer: ReturnType<typeof setTimeout> | null = null
  private running = false
  /** A stopped or superseded startup must not make the email listener ready. */
  private startup = 0
  /** The live feed's socket, open or opening. */
  private socket: FeedSocket | null = null
  /** The socket is open: it brings the changes, and the store does not poll. */
  private live = false
  /** When the open socket opened. */
  private openedAt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  /** The sockets that failed in a row. */
  private attempts = 0
  /** Until when, after a restart close, the reconnection waits stay short. */
  private restartUntil = 0
  /** Snapshots on their way: no socket opens from a cursor about to change. */
  private snapshots = 0

  /** Captures the member, so a control surviving sign-in cannot act as the next person. */
  notifications() {
    const as = this.state.me?.member?.id
    return {
      push: () => this.api.pushStatus(as),
      registerPushDevice: (args: import('../api/types').PushRegistration) => this.api.registerPushDevice(args, as),
      removePushDevice: (id: string) => this.api.removePushDevice(id, as),
      testPush: () => this.api.testPush(as),
      presence: (here: boolean) => this.api.pushPresence(here, as),
    }
  }

  /** The iPhone app's notices (Poulpe-627), for the member signed in now, as notifications() captures it. */
  phoneNotifications() {
    const as = this.state.me?.member?.id
    return {
      registerPhone: (args: import('../api/types').PhoneRegistration) => this.api.registerPhone(args, as),
      forgetPhone: () => this.api.forgetPhone(as),
    }
  }

  private readonly api: Api
  private readonly passkeys: Passkeys
  /** A bearer token signs the pages in: there is no signing in or out. */
  readonly byToken: boolean
  private readonly sessions: SessionChannel | null
  private readonly emailLinks: BoardStoreOptions['emailLinks']
  private readonly onEmailLinksReady: (ready: boolean) => void
  /** Each email link finished or being finished, by attempt: one finish a link, whoever asks. Kept across sign-ins. */
  private readonly emailFinishes = new Map<string, Promise<EmailLinkOutcome>>()
  /** The Mac app's shell hands over a link: `{attempt, token}`. */
  private readonly emailLinkHeard = (event: Event) => {
    const detail = (event as CustomEvent<unknown>).detail as { attempt?: unknown; token?: unknown } | null
    if (typeof detail?.attempt !== 'string' || typeof detail.token !== 'string' || !detail.attempt || !detail.token) return
    void this.finishEmailLink(detail.attempt, detail.token).then((outcome) => {
      // Signed in, no page shows it.
      if (!outcome.ok && this.state.status !== 'signed_out') this.onFailure(emailLinkProblemText(outcome))
    })
  }
  /** Another tab signed in or out. */
  private readonly sessionChanged = () => void this.refreshSession()
  private readonly pollMs: number
  private readonly feed: OpenFeed | null
  private readonly reconnectMs: (attempt: number) => number
  private readonly pageSize: number
  private readonly onFailure: (message: string) => void
  private readonly onSessionState: BoardStoreOptions['onSessionState']
  private offlineSessionAllowed: boolean
  private readonly newMutationId: () => string
  private readonly newId: () => string

  constructor(options: BoardStoreOptions) {
    this.api = options.api
    this.origin = options.origin ?? globalThis.location?.origin ?? 'poulpe'
    this.useLocal = options.local ?? true
    this.passkeys = options.passkeys ?? browserPasskeys
    this.byToken = options.byToken ?? false
    this.emailLinks = options.byToken ? null : (options.emailLinks ?? null)
    this.onEmailLinksReady = options.onEmailLinksReady ?? (() => {})
    this.sessions = options.sessions ?? null
    this.pollMs = options.pollMs ?? 2_000
    this.feed = options.feed ?? null
    this.reconnectMs = options.reconnectMs ?? reconnectBackoff
    this.pageSize = options.pageSize ?? 200
    this.onFailure = options.onFailure ?? (() => {})
    this.onSessionState = options.onSessionState
    this.offlineSessionAllowed = options.offlineSessionAllowed ?? true
    this.newMutationId = options.newMutationId ?? uuid4
    this.newId = options.newId ?? (() => uuid7())
  }

  // --- React binding (useSyncExternalStore) ----------------------------------

  getState = (): BoardState => this.state

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  // --- lifecycle ---------------------------------------------------------------

  /** Read who is signed in, take a snapshot, then follow the feed: live when it can, by polling otherwise. */
  async start(): Promise<void> {
    const startup = ++this.startup
    this.running = true
    this.sessions?.addEventListener('message', this.sessionChanged)
    this.emailLinks?.addEventListener('poulpe-email-link', this.emailLinkHeard)
    await this.load()
    if (!this.running || startup !== this.startup) return
    // An initial 401 forgets the old epoch. A queued link must start after
    // that decision, including a newer session read which replaced load's.
    await this.waitForSessionDecision()
    if (!this.running || startup !== this.startup) return
    if (this.emailLinks) this.onEmailLinksReady(true)
    this.schedule()
  }

  /**
   * Read who is signed in, unless the store knows, then take a snapshot. A
   * newer read of who is signed in, made meanwhile, replaces this one: it
   * loads the board when it finds someone, or says why it could not.
   */
  async load(): Promise<void> {
    if (!this.state.me) {
      const epoch = this.epoch
      const read = ++this.sessionRead
      try {
        if (this.state.status === 'error') this.setState({ status: 'loading', error: null })
        let me: Me
        try {
          me = await this.readMe(read)
        } catch (err) {
          if (epoch === this.epoch && read === this.sessionRead && !this.signedOutBy(err)) {
            if (!isOffline(err) || !await this.restoreOffline(epoch, read)) this.showError(err)
          }
          return
        }
        if (epoch !== this.epoch || read !== this.sessionRead) return
        this.sessionState('confirmed')
        this.setState({ me })
        try {
          await this.connectLocal(me, epoch, read)
        } catch (err) {
          if (epoch === this.epoch && read === this.sessionRead) this.showError(err)
          return
        }
        if (epoch !== this.epoch || read !== this.sessionRead) return
      } finally {
        this.sessionDecided(read)
      }
    }
    await this.takeSnapshot()
  }

  stop(): void {
    this.startup++
    this.running = false
    this.sessions?.removeEventListener('message', this.sessionChanged)
    this.emailLinks?.removeEventListener('poulpe-email-link', this.emailLinkHeard)
    if (this.emailLinks) this.onEmailLinksReady(false)
    this.generation++
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    if (this.retryTimer !== null) clearTimeout(this.retryTimer)
    this.retryTimer = null
    this.closeFeed()
    this.persistSoon(true)
  }

  /** Poll, unless a socket brings the feed. */
  private schedule() {
    if (!this.running || this.pollMs <= 0 || this.timer !== null || this.live) return
    this.timer = setTimeout(() => {
      this.timer = null
      const { status } = this.state
      // Signing in starts the board again; an open socket stopped polling.
      if (status === 'signed_out' || this.live) return
      // A board that never loaded tries again rather than the feed.
      const read = status === 'ready' ? this.catchUp() : this.load()
      void read.finally(() => this.schedule())
    }, this.pollMs)
  }

  // --- the live feed -------------------------------------------------------------

  /**
   * Open the socket from the cursor, while the store runs and the board is
   * loaded, unless one is open or opening, one is due after a backoff, or a
   * snapshot is about to move the cursor.
   */
  private follow(): void {
    if (!this.feed || !this.running || this.state.status !== 'ready') return
    if (this.socket !== null || this.reconnectTimer !== null || this.snapshots > 0) return
    const epoch = this.epoch
    let socket: FeedSocket | null = null
    // What a socket sends counts while it is the store's, in the epoch it opened in.
    const current = () => socket !== null && socket === this.socket && epoch === this.epoch
    try {
      socket = this.feed(
        this.cursor,
        {
          open: () => {
            if (current()) this.feedOpened()
          },
          change: (change) => {
            if (current()) this.feedChange(change)
          },
          closed: (code) => {
            if (current()) this.feedClosed(code)
          },
        },
        this.actingAs(),
        this.feedEpoch,
      )
    } catch {
      // The browser would not even try: as a socket that failed.
      this.feedClosed(FEED_ABNORMAL)
      return
    }
    this.socket = socket
  }

  private feedOpened(): void {
    this.live = true
    if (this.state.stream !== 'live') this.setState({ stream: 'live' })
    this.openedAt = Date.now()
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    // Catch up before replaying: a socket opening does not mean its backlog arrived.
    if (this.state.offline || this.outbox.length) void this.catchUp()
  }

  /** A change from the socket: the next after the cursor, or one the store has. */
  private feedChange(change: Change): void {
    if (change.seq > this.cursor + 1) {
      // The socket is out of step with the store: open another from the cursor.
      this.socket?.close()
      this.feedClosed(FEED_ABNORMAL)
      return
    }
    this.applyChanges([change])
  }

  /**
   * The socket closed: poll, and open another after a backoff; first take a
   * snapshot, or read who is signed in, when the close says to. Polling and
   * the next socket go on while that read is on its way: each names the
   * member the store shows, so the server lets neither act as anyone else.
   */
  private feedClosed(code: number): void {
    this.socket = null
    if (this.live && Date.now() - this.openedAt >= FEED_STABLE_MS) this.attempts = 0
    if (code === FEED_RESTART) {
      this.attempts = 0
      this.restartUntil = Date.now() + RESTART_WINDOW_MS
    }
    this.live = false
    if (!this.running) return
    if (this.state.stream !== 'reconnecting') this.setState({ stream: 'reconnecting' })
    this.schedule()
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      this.follow()
    }, this.reconnectDelay())
    if (code === FEED_SNAPSHOT_NEEDED) void this.takeSnapshot(true)
    else if (code === FEED_UNAUTHORIZED) this.sessionMayHaveChanged()
  }

  /** The wait before the next socket: the backoff, within the restart's cap while it lasts. */
  private reconnectDelay(): number {
    const wait = this.reconnectMs(this.attempts++)
    return Date.now() < this.restartUntil ? Math.min(wait, RESTART_RETRY_MS) : wait
  }

  /** Close the socket, and open no other until the store follows the feed again. */
  private closeFeed(): void {
    const socket = this.socket
    this.socket = null
    this.live = false
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    socket?.close()
    if (this.state.stream !== 'none') this.setState({ stream: 'none' })
  }

  private baseForDisk(): LocalBase {
    return {
      snapshot: {
        seq: this.cursor, ...(this.feedEpoch === null ? {} : { epoch: this.feedEpoch }), projects: [...this.projectBase.values()], labels: [...this.labelBase.values()],
        issues: [...this.issueBase.values()], record_entries: [...this.entryBase.values()],
        questions: [...this.questionBase.values()], sessions: [...this.sessionBase.values()],
        proposals: [...this.proposalBase.values()], returned: [...this.returnedBase.values()], landings: [...this.landingBase.values(), ...this.heldBase.values()],
        attachments: [...this.attachmentBase.values()], plans: [...this.planBase.values()], runs: [...this.runBase.values()],
        runners: [...this.runnerBase.values()], jobs: [...this.jobBase.values()],
      },
      snapshotSeq: this.snapshotSeq,
      rowSeq: [...this.rowSeq],
    }
  }

  /** Capture the database and rows together; an old epoch never writes into a new identity. */
  private persistSoon(flush = false): void {
    if (this.persistTimer !== null) {
      if (!flush) return
      clearTimeout(this.persistTimer)
      this.persistTimer = null
    }
    if (!this.local) return
    const save = () => {
      this.persistTimer = null
      const local = this.local
      const epoch = this.epoch
      if (local) void local.replace(this.baseForDisk()).catch((err: unknown) => {
        if (epoch === this.epoch) this.onFailure(errorMessage(err))
      })
    }
    if (flush) save()
    else this.persistTimer = setTimeout(save, 100)
  }

  private restoreBase(base: LocalBase): void {
    const s = base.snapshot
    this.projectBase = new Map(s.projects.map((p) => [p.id, p]))
    this.labelBase = new Map(s.labels.map((l) => [l.id, l]))
    this.issueBase = new Map(s.issues.map((i) => [i.id, i]))
    this.entryBase = new Map(s.record_entries.map((e) => [e.id, e]))
    this.questionBase = new Map(s.questions.map((q) => [q.id, q]))
    this.sessionBase = new Map(s.sessions.map((s) => [s.id, s]))
    this.proposalBase = new Map(s.proposals.map((p) => [p.id, p]))
    this.returnedBase = new Map((s.returned ?? []).map((p) => [p.id, p]))
    this.landingBase = new Map(s.landings.filter((l) => l.state !== 'finished').map((l) => [l.id, l]))
    this.heldBase = new Map(s.landings.filter((l) => l.state === 'finished').map((l) => [l.id, l]))
    this.planBase = new Map((s.plans ?? []).map((p) => [p.id, p]))
    this.attachmentBase = new Map((s.attachments ?? []).map((a) => [a.id, a]))
    this.runBase = new Map((s.runs ?? []).map((r) => [r.id, r]))
    this.runnerBase = new Map((s.runners ?? []).map((r) => [r.id, r]))
    this.jobBase = new Map((s.jobs ?? []).map((j) => [j.id, j]))
    this.cursor = s.seq
    this.feedEpoch = s.epoch ?? null
    this.snapshotSeq = base.snapshotSeq
    this.rowSeq.clear()
    for (const [id, seq] of base.rowSeq) this.rowSeq.set(id, seq)
  }

  private async connectLocal(me: Me, epoch: number, read: number): Promise<void> {
    if (!this.useLocal || this.localUnavailable || !me.member || this.local) return
    let local: LocalStore | null = null
    let saved: Awaited<ReturnType<Awaited<ReturnType<typeof openLocal>>['read']>>
    try {
      local = await openLocal(this.origin, me.workspace.id, me.member.id)
      saved = await local.read()
    } catch (err) {
      local?.close()
      if (epoch !== this.epoch || read !== this.sessionRead) return
      // Only an explicit storage refusal or missing API permits memory mode.
      // Transient failures must not disable durability for the session.
      if (!(err instanceof DOMException && err.name === 'SecurityError') && typeof indexedDB !== 'undefined') throw err
      this.localUnavailable = true
      console.warn('Poulpe keeps no changes on this device', err)
      return
    }
    if (epoch !== this.epoch || read !== this.sessionRead) { local.close(); return }
    this.local = local
    this.outbox = saved.outbox
    this.pending = [...saved.outbox]
    this.setState({ refused: saved.refused })
    // Restored rows are also useful if /me worked but the snapshot loses the network.
    if (saved.base) {
      this.restoreBase(saved.base)
      // The plans read before show at once, marked offline like the rest, until the server answers.
      try {
        for (const detail of await local.readPlans()) if (this.planBase.has(detail.id) && !this.detailBase.has(detail.id)) this.detailBase.set(detail.id, detail)
      } catch { /* the plans are read from the server instead */ }
      if (epoch !== this.epoch || read !== this.sessionRead) return
      this.setState({ status: 'ready', offline: true, ...this.visible() })
    }
    await rememberIdentity(this.origin, { workspaceId: me.workspace.id, memberId: me.member.id })
  }

  /** The open identity's storage only; unavailable storage and memory fixtures keep no transcripts. */
  transcriptCache(): LocalStore | null { return this.local }

  /** Unresolved composer sends belong to the same identity as the board cache. */
  promptCache(): LocalStore | null { return this.local }

  async keepPrompt(prompt: PendingPrompt, cache: LocalStore | null): Promise<void> {
    if (cache !== this.local) throw new Error('The signed-in person changed. The prompt was not sent.')
    if (cache) await cache.keepPrompt(prompt)
    else if (this.useLocal) throw new Error('The prompt could not be saved on this device. Try again.')
    // Memory-only fixtures explicitly opt out of device storage.
  }

  private async restoreOffline(epoch: number, read: number): Promise<boolean> {
    if (!this.useLocal || this.byToken || !this.offlineSessionAllowed) return false
    try {
      const identity = await lastIdentity(this.origin)
      if (!identity || epoch !== this.epoch || read !== this.sessionRead) return false
      const me: Me = {
        workspace: { id: identity.workspaceId, name: '' },
        member: { id: identity.memberId, name: '' },
        actor: { kind: 'person', member: identity.memberId },
      }
      await this.connectLocal(me, epoch, read)
      if (epoch !== this.epoch || read !== this.sessionRead || this.state.status !== 'ready') return false
      this.setState({ me, offline: true, ...this.visible() })
      this.follow()
      return true
    } catch { return false }
  }

  /**
   * Replace every confirmed row with the server's snapshot, and follow the
   * feed from its seq. Pending changes stay on top until they are settled.
   * The last snapshot on its way to end, whether it was kept, dropped or
   * failed, opens the socket, unless one is open or due (`follow`).
   *
   * `feedAsked`: the feed cannot go on from the cursor (410 or 4410). The
   * server may then have lost the seqs the store knows, restored from an
   * older dump, so they no longer say which snapshot is newer: the store
   * forgets them, and keeps the snapshot it asked for last (Poulpe-784).
   */
  async takeSnapshot(feedAsked = false): Promise<void> {
    const generation = ++this.generation
    if (feedAsked) {
      this.snapshotSeq = 0
      this.rowSeq.clear()
    }
    if (this.state.status === 'error') this.setState({ status: 'loading', error: null })
    this.snapshots++
    try {
      const snapshot = await this.api.snapshot(this.actingAs())
      if (generation !== this.generation) return
      if (snapshot.seq < this.snapshotSeq || [...this.rowSeq.values()].some((seq) => seq > snapshot.seq)) return
      this.projectBase = new Map(snapshot.projects.map((p) => [p.id, p]))
      // A server from before labels sends none.
      this.labelBase = new Map((snapshot.labels ?? []).filter((l) => l.deleted_at === null).map((l) => [l.id, l]))
      this.issueBase = new Map(snapshot.issues.map((i) => [i.id, i]))
      this.entryBase = new Map(snapshot.record_entries.map((e) => [e.id, e]))
      this.replaceQuestions(snapshot.questions)
      // A server that keeps no sessions yet sends none.
      this.sessionBase = new Map((snapshot.sessions ?? []).map((s) => [s.id, s]))
      // Nor, without a queue, proposals or landings.
      this.proposalBase = new Map((snapshot.proposals ?? []).filter((p) => p.state === 'open').map((p) => [p.id, p]))
      // Nor, before Stuck, the returned ones.
      this.returnedBase = new Map((snapshot.returned ?? []).filter((p) => p.state === 'returned').map((p) => [p.id, p]))
      this.landingBase = new Map((snapshot.landings ?? []).filter((l) => l.state !== 'finished').map((l) => [l.id, l]))
      this.heldBase = new Map((snapshot.landings ?? []).filter((l) => l.state === 'finished').map((l) => [l.id, l]))
      // Nor, before plans, any plan.
      this.planBase = new Map((snapshot.plans ?? []).filter((p) => p.archived_at === null).map((p) => [p.id, p]))
      this.attachmentBase = new Map((snapshot.attachments ?? []).map((a) => [a.id, a]))
      // Nor, before runs, any run.
      this.runBase = new Map((snapshot.runs ?? []).map((r) => [r.id, r]))
      // Nor, before Poulpe-367, any runner or job.
      this.runnerBase = new Map((snapshot.runners ?? []).map((r) => [r.id, r]))
      this.jobBase = new Map((snapshot.jobs ?? []).filter((j) => jobOpen(j)).map((j) => [j.id, j]))
      this.rowSeq.clear()
      this.snapshotSeq = snapshot.seq
      this.cursor = snapshot.seq
      this.feedEpoch = snapshot.epoch ?? null
      this.setState({ status: 'ready', error: null, offline: false, ...this.visible() })
      // A snapshot skips feed history, including comments, which have no body revision.
      for (const id of this.watched.keys()) void this.readPlan(id)
      if (this.local) await this.local.replace(this.baseForDisk())
      if (generation === this.generation) await this.drain()
    } catch (err) {
      if (generation !== this.generation) return
      this.failed(err)
    } finally {
      this.snapshots--
      // The last to end may be an older one, dropped: a newer one that ended
      // first could not follow while this one was on its way.
      this.follow()
    }
  }

  /**
   * A read of the board failed: a 401 has the store read who is signed in.
   * Otherwise a board that has loaded keeps what it shows, and the next poll
   * tries again.
   */
  private failed(err: unknown): void {
    if (isOffline(err)) this.setState({ offline: true })
    if (!this.unauthorized(err)) this.showError(err)
  }

  /** Say why the board could not load; a board that has loaded keeps what it shows. */
  private showError(err: unknown): void {
    if (this.state.status !== 'ready') this.setState({ status: 'error', error: asApiError(err) })
  }

  /**
   * True, once the store has asked who is signed in (`refreshSession`), when
   * `err` is a 401: the request had no session, or named another member than
   * its session's (`identity_changed`). That read decides what follows.
   */
  private unauthorized(err: unknown): boolean {
    if (this.byToken || !(err instanceof ApiError) || err.status !== 401) return false
    this.sessionMayHaveChanged()
    return true
  }

  /**
   * A 401 or a 4401: read who is signed in, unless a read is on its way, which
   * then answers for it. Asking again would replace that read before it
   * lands, and polls that keep failing would do so for ever.
   */
  private sessionMayHaveChanged(): void {
    if (this.sessionLanded >= this.sessionRead) void this.refreshSession()
  }

  /** `/v1/me`, as read number `read`: once it answers, that read has landed. */
  private async readMe(read: number): Promise<Me> {
    try {
      return await this.api.me()
    } finally {
      this.sessionLanded = Math.max(this.sessionLanded, read)
    }
  }

  /**
   * True, once the store has forgotten the board, when `err` is the 401 of
   * its own read of who is signed in: there is no session.
   */
  private signedOutBy(err: unknown): boolean {
    if (this.byToken || !(err instanceof ApiError) || err.status !== 401) return false
    this.forget({ status: 'signed_out' })
    this.sessionState('invalidated')
    return true
  }

  private sessionState(state: 'confirmed' | 'authenticated' | 'invalidated'): void {
    if (this.byToken) return
    if (this.onSessionState) this.offlineSessionAllowed = state !== 'invalidated'
    this.onSessionState?.(state)
  }

  /** The member the pages act as, which each request names: none until it is known, or for an agent's token. */
  private actingAs(): string | null {
    return this.state.me?.member?.id ?? null
  }

  /**
   * Drop the board, the pending changes and the cursor, and stop reading the
   * feed; then show `next`. An answer to a request made before is dropped.
   */
  private forget(next: Partial<BoardState>): void {
    this.persistSoon(true)
    // Signed out: no file read before stays on this device. The clear is issued before the close.
    if (next.status === 'signed_out') void this.local?.clearFiles().catch(() => {})
    this.local?.close()
    this.local = null
    this.outbox = []
    this.draining = null
    this.sendingId = null
    this.durable.clear()
    this.failures.clear()
    this.confirmations.clear()
    for (const resolve of this.outcomes.values()) resolve(false)
    this.outcomes.clear()
    this.epoch++
    this.generation++
    if (this.timer !== null) clearTimeout(this.timer)
    this.timer = null
    if (this.retryTimer !== null) clearTimeout(this.retryTimer)
    this.retryTimer = null
    this.closeFeed()
    this.attempts = 0
    this.projectBase = new Map()
    this.labelBase = new Map()
    this.issueBase = new Map()
    this.entryBase = new Map()
    this.questionBase = new Map()
    this.settledBase = new Map()
    this.sessionBase = new Map()
    this.proposalBase = new Map()
    this.returnedBase = new Map()
    this.landingBase = new Map()
    this.heldBase = new Map()
    this.planBase = new Map()
    this.attachmentBase = new Map()
    this.runBase = new Map()
    this.runnerBase = new Map()
    this.jobBase = new Map()
    this.detailBase = new Map()
    this.detailErrors = new Map()
    this.planReads.clear()
    this.pending = []
    this.rowSeq.clear()
    this.snapshotSeq = 0
    this.cursor = 0
    this.feedEpoch = null
    this.setState({ ...EMPTY, ...next })
    if (next.status === 'signed_out' && this.useLocal) void rememberIdentity(this.origin, null).catch(() => {})
  }

  // --- signing up, in and out -------------------------------------------------------

  /** Sign up with a new passkey, into a new workspace; the board then loads. */
  signUp(name: string, workspace: string): Promise<SignInOutcome> {
    return this.ceremony(async () => {
      const { ceremony, options } = await this.api.signUpStart({ name, workspace })
      const credential = await this.passkeys.create(options)
      return this.api.signUpFinish({ ceremony, credential })
    })
  }

  /** Whether sign-up is closed now. */
  signUpStatus(): Promise<SignUpStatus> {
    return this.api.signUpStatus()
  }

  /**
   * Make a code that registers one runner in the workspace, on the person's
   * behalf, for ten minutes: they confirm it with a passkey
   * (`register_runner`, and the SHA-256 of the empty string), unless their
   * account asks for none (`confirmedAction`), and the server then makes
   * it. The code is answered once, and kept nowhere here.
   */
  makeRunnerCode(): Promise<ConfirmedOutcome<RunnerCode>> {
    return this.confirmedAction(() => REGISTER_RUNNER, (as) => this.api.runnerCode(as), 'Only a person makes a runner code.')
  }

  /**
   * Have the server take an action that a person confirms with a passkey:
   * the confirmation first (`confirmThenPost`), unless their account asks
   * for none. Then `post` goes first, and a passkey is asked for only if the
   * server still answers `unconfirmed`, the setting having been turned back
   * on elsewhere (`confirmedIfAsked`).
   */
  private confirmedAction<T>(
    action: (me: Me) => ConfirmAction | Promise<ConfirmAction>,
    post: (as: string | null) => Promise<T>,
    onlyAPerson: string,
  ): Promise<ConfirmedOutcome<T>> {
    return confirmsActions(this.state.me) ? this.confirmThenPost(action, post, onlyAPerson) : this.confirmedIfAsked(action, post, onlyAPerson)
  }

  /**
   * Have the server take an action that needs a fresh confirmation of
   * exactly it, which only a person takes (`onlyAPerson` says so when the
   * store acts as no person): they confirm `action` with a passkey
   * (`confirm/start`, the browser's prompt, `confirm/finish`), and `post`
   * then asks for it. Every step names the member the page showed, and none
   * follows once the store's identity has changed. What `post` answers is
   * the caller's, and kept nowhere here.
   */
  private async confirmThenPost<T>(
    action: (me: Me) => ConfirmAction | Promise<ConfirmAction>,
    post: (as: string | null) => Promise<T>,
    onlyAPerson: string,
  ): Promise<ConfirmedOutcome<T>> {
    if (!this.passkeys.supported()) return { ok: false, problem: 'unsupported', message: 'This browser has no passkeys.' }
    const me = this.state.me
    if (!me?.member) return { ok: false, problem: 'failed', message: onlyAPerson }
    const epoch = this.epoch
    const as = this.actingAs()
    const changed = () => epoch !== this.epoch
    const identityChanged: ConfirmedOutcome<T> = { ok: false, problem: 'identity_changed', message: 'The identity changed; try again.' }
    try {
      const confirming = await action(me)
      if (changed()) return identityChanged
      const { ceremony, options } = await this.api.confirmStart(confirming, as)
      if (changed()) return identityChanged
      const credential = await this.passkeys.get(options)
      if (changed()) return identityChanged
      await this.api.confirmFinish({ ceremony, credential }, as)
      if (changed()) return identityChanged
      return { ok: true, made: await post(as) }
    } catch (err) {
      if (changed()) return identityChanged
      this.unauthorized(err)
      return confirmationProblem(err)
    }
  }

  /** Sign in with a passkey the person picks; the board then loads. */
  signIn(): Promise<SignInOutcome> {
    return this.ceremony(async () => {
      const { ceremony, options } = await this.api.signInStart()
      const credential = await this.passkeys.get(options)
      return this.api.signInFinish({ ceremony, credential })
    })
  }

  private async ceremony(run: () => Promise<Me>): Promise<SignInOutcome> {
    if (!this.passkeys.supported()) return { ok: false, problem: 'unsupported', message: 'This browser has no passkeys.' }
    let me: Me
    try {
      me = await run()
    } catch (err) {
      return signInProblem(err)
    }
    return this.enter(me)
  }

  /** Signed in as `me`: forget the board, tell the other tabs, and load theirs. */
  private async enter(me: Me): Promise<SignInOutcome> {
    this.sessionState('authenticated')
    this.forget({ me })
    this.sessions?.postMessage('signed_in')
    const epoch = this.epoch
    const read = this.sessionRead
    try {
      await this.connectLocal(me, epoch, read)
      if (epoch !== this.epoch || read !== this.sessionRead) return { ok: true }
      await this.takeSnapshot()
    } catch (err) {
      if (epoch === this.epoch && read === this.sessionRead) this.showError(err)
      return signInProblem(err)
    }
    this.schedule()
    return { ok: true }
  }

  /** End the session, and forget the board. False, once `onFailure` has been told, when the server did not. */
  async signOut(): Promise<boolean> {
    try {
      await this.api.signOut()
    } catch (err) {
      this.onFailure(`Could not sign out: ${errorMessage(err)}`)
      return false
    }
    this.forget({ status: 'signed_out' })
    this.sessionState('invalidated')
    this.sessions?.postMessage('signed_out')
    return true
  }

  // --- email links, and the account's address and passkeys ----------------------------

  /**
   * Ask for a link that signs in, or up when `names` are given, from the page
   * at `back`: a new verifier, kept here by the attempt the server answers,
   * whose challenge alone is sent. The server answers alike whatever the
   * address's account, and so does this.
   */
  async startEmail(address: string, names: { name: string; workspace: string } | null, back: string | null): Promise<EmailOutcome> {
    const verifier = newVerifier()
    try {
      const challenge = await challengeOf(verifier)
      const app = emailLinkApp()
      const started = await this.api.emailStart({ ...(names ? { address, challenge, purpose: 'signup', ...names } : { address, challenge, purpose: 'signin' }), ...(app ? { app } : {}) })
      keepVerifier(started.attempt, { verifier, expires_at: started.expires_at, back })
      this.clearEmailLink()
      return { ok: true }
    } catch (err) {
      return { ok: false, message: emailStartProblem(err, names ? 'sign up' : 'sign in') }
    }
  }

  /**
   * Finish an email link: sign in or up with it, or, signed in as its person
   * already, take their new address. One finish a link: asked again, as by
   * the page that loads once signed in, it answers what the first did; but
   * one that failed on the way may be tried again. The verifier is forgotten
   * once the server has answered; without one here, nothing is sent.
   */
  finishEmailLink(attempt: string, token: string): Promise<EmailLinkOutcome> {
    const known = this.emailFinishes.get(attempt)
    if (known) return known
    // A link's page loaded again has lost its token with its fragment: nothing to send, and its verifier stays.
    if (!token) return Promise.resolve({ ok: false, problem: 'refused', message: 'The link has no token.' })
    const finishing = this.finishLink(attempt, token)
    this.emailFinishes.set(attempt, finishing)
    void finishing.then((outcome) => {
      if (!outcome.ok && outcome.problem === 'failed') this.emailFinishes.delete(attempt)
    })
    return finishing
  }

  private async finishLink(attempt: string, token: string): Promise<EmailLinkOutcome> {
    const epoch = this.epoch
    const signedOut = this.state.status === 'signed_out'
    const show = (emailLink: EmailLinkState) => {
      if (epoch === this.epoch && signedOut && this.state.status === 'signed_out') this.setState({ emailLink })
    }
    const failed = (outcome: Extract<EmailLinkOutcome, { ok: false }>) => {
      show({ phase: 'failed', problem: outcome.problem, message: outcome.message })
      return outcome
    }
    const kept = keptVerifier(attempt)
    if (!kept) return failed({ ok: false, problem: 'elsewhere', message: 'This browser keeps no verifier for the link.' })
    show({ phase: 'finishing' })
    let me: Me
    try {
      me = await this.api.emailFinish({ attempt, token, verifier: kept.verifier })
    } catch (err) {
      if (!isOffline(err)) forgetVerifier(attempt)
      return failed({ ok: false, problem: err instanceof ApiError && err.reason === EMAIL_LINK_REFUSED ? 'refused' : 'failed', message: errorMessage(err) })
    }
    forgetVerifier(attempt)
    if (epoch !== this.epoch) return { ok: false, problem: 'refused', message: 'The identity changed; try again.' }
    if (this.state.status !== 'signed_out' && sameIdentity(me, this.state.me)) {
      // The same person: an address added, or a sign-in again. Nothing is forgotten.
      this.setState({ me })
      this.sessionState('authenticated')
      this.sessions?.postMessage('signed_in')
      return { ok: true, back: kept.back }
    }
    const entered = await this.enter(me)
    return entered.ok ? { ok: true, back: kept.back } : failed({ ok: false, problem: 'failed', message: entered.message })
  }

  /** Sign-in shows its form again, not what became of the last link. */
  clearEmailLink(): void {
    if (this.state.emailLink) this.setState({ emailLink: null })
  }

  /**
   * Ask for a link that adds `address` to the person, from the page at
   * `back`: confirmed with a passkey (`add_email`, people/actions.ts) only
   * when the server asks for it.
   */
  async addEmail(address: string, back: string | null): Promise<AccountOutcome<EmailStarted>> {
    const epoch = this.epoch
    const identityChanged: ConfirmedOutcome<EmailStarted> = { ok: false, problem: 'identity_changed', message: 'The identity changed; try again.' }
    const verifier = newVerifier()
    const challenge = await challengeOf(verifier)
    if (epoch !== this.epoch) return identityChanged
    const app = emailLinkApp()
    const outcome = await this.confirmedIfAsked(() => addEmailAction(address), (as) => this.api.emailAddStart({ address, challenge, ...(app ? { app } : {}) }, as), 'Only a person adds an address.')
    if (epoch !== this.epoch) return identityChanged
    if (outcome.ok) keepVerifier(outcome.made.attempt, { verifier, expires_at: outcome.made.expires_at, back })
    return outcome
  }

  /** The person's passkeys. */
  passkeyList(): Promise<PasskeyList> {
    return this.api.passkeys(this.actingAs())
  }

  /**
   * Add a passkey: confirmed with one they have (`add_passkey`) only when the
   * server asks for it; with none to confirm with, `not_fresh`. Then the
   * browser's prompt makes it, and the server answers the person's passkeys.
   */
  async addPasskey(): Promise<AccountOutcome<PasskeyList>> {
    if (!this.passkeys.supported()) return { ok: false, problem: 'unsupported', message: 'This browser has no passkeys.' }
    const epoch = this.epoch
    const as = this.actingAs()
    const started = await this.confirmedIfAsked<Started<CreationOptions>>(() => ADD_PASSKEY, (as) => this.api.passkeyAddStart(as), 'Only a person adds a passkey.', async (as) => (await this.api.passkeys(as)).count > 0)
    if (!started.ok) return started
    const identityChanged: ConfirmedOutcome<PasskeyList> = { ok: false, problem: 'identity_changed', message: 'The identity changed; try again.' }
    try {
      if (epoch !== this.epoch) return identityChanged
      const credential = await this.passkeys.create(started.made.options)
      if (epoch !== this.epoch) return identityChanged
      return { ok: true, made: await this.api.passkeyAddFinish({ ceremony: started.made.ceremony, credential }, as) }
    } catch (err) {
      if (epoch !== this.epoch) return identityChanged
      this.unauthorized(err)
      return confirmationProblem(err)
    }
  }

  /** Name the person's passkey `id`, or clear its name with null; the server answers their passkeys. */
  async namePasskey(id: string, name: string | null): Promise<ConfirmedOutcome<PasskeyList>> {
    const epoch = this.epoch
    const as = this.actingAs()
    try {
      return { ok: true, made: await this.api.passkeyName(id, name, as) }
    } catch (err) {
      if (epoch !== this.epoch) return { ok: false, problem: 'identity_changed', message: 'The identity changed; try again.' }
      this.unauthorized(err)
      return confirmationProblem(err)
    }
  }

  /**
   * Remove the person's passkey `id`: confirmed with one they have, for
   * exactly it (`remove_passkey`), when the server asks, as it always does.
   * It refuses their last passkey before asking, when they have no address
   * to sign in with instead (`last_passkey`), and no prompt shows. The server
   * answers their remaining passkeys. A person whose account asks for no
   * confirmation removes one from a browser without passkeys too.
   */
  async removePasskey(id: string): Promise<PasskeyRemoval> {
    if (confirmsActions(this.state.me) && !this.passkeys.supported()) return { ok: false, problem: 'unsupported', message: 'This browser has no passkeys.' }
    const last = async (as: string | null): Promise<PasskeyList | typeof LAST_PASSKEY> => {
      try {
        return await this.api.passkeyRemove(id, as)
      } catch (err) {
        if (err instanceof ApiError && err.reason === LAST_PASSKEY) return LAST_PASSKEY
        throw err
      }
    }
    const outcome = await this.confirmedIfAsked(() => removePasskeyAction(id), last, 'Only a person removes a passkey.')
    if (!outcome.ok) return outcome
    if (outcome.made === LAST_PASSKEY) return { ok: false, problem: 'last_passkey', message: 'This is the last passkey.' }
    return { ok: true, made: outcome.made }
  }

  /**
   * `post`, as the person the page showed; and, when the server refuses it
   * unconfirmed, again once they confirm `action` with a passkey
   * (`confirmThenPost`). `canConfirm` says whether they have one to confirm
   * with: when not, `not_fresh`, and no prompt shows.
   */
  private confirmedIfAsked<T>(
    action: (me: Me) => ConfirmAction | Promise<ConfirmAction>,
    post: (as: string | null) => Promise<T>,
    onlyAPerson: string,
  ): Promise<ConfirmedOutcome<T>>
  private confirmedIfAsked<T>(
    action: (me: Me) => ConfirmAction | Promise<ConfirmAction>,
    post: (as: string | null) => Promise<T>,
    onlyAPerson: string,
    canConfirm: (as: string | null) => Promise<boolean>,
  ): Promise<AccountOutcome<T>>
  private async confirmedIfAsked<T>(
    action: (me: Me) => ConfirmAction | Promise<ConfirmAction>,
    post: (as: string | null) => Promise<T>,
    onlyAPerson: string,
    canConfirm?: (as: string | null) => Promise<boolean>,
  ): Promise<AccountOutcome<T>> {
    if (!this.state.me?.member) return { ok: false, problem: 'failed', message: onlyAPerson }
    const epoch = this.epoch
    const as = this.actingAs()
    const identityChanged: ConfirmedOutcome<T> = { ok: false, problem: 'identity_changed', message: 'The identity changed; try again.' }
    try {
      return { ok: true, made: await post(as) }
    } catch (err) {
      if (epoch !== this.epoch) return identityChanged
      if (!(err instanceof ApiError && err.reason === UNCONFIRMED)) {
        this.unauthorized(err)
        return confirmationProblem(err)
      }
    }
    if (canConfirm) {
      try {
        const can = await canConfirm(as)
        if (epoch !== this.epoch) return identityChanged
        if (!can) return { ok: false, problem: 'not_fresh', message: 'There is no passkey to confirm with, and the session is too old to go without.' }
      } catch (err) {
        if (epoch !== this.epoch) return identityChanged
        this.unauthorized(err)
        return confirmationProblem(err)
      }
    }
    return this.confirmThenPost(action, post, onlyAPerson)
  }

  /**
   * Set whether the person confirms their actions with a passkey (Poulpe-686).
   * Turning it off while the server holds it on is confirmed with a passkey
   * (`stop_confirmations`), asked for when the server says so; turning it on
   * takes none. The server answers who the person is, which shows at once.
   */
  async setConfirmations(on: boolean): Promise<ConfirmedOutcome<Me>> {
    const epoch = this.epoch
    const outcome = await this.confirmedIfAsked(() => STOP_CONFIRMATIONS, (as) => this.api.setConfirmations(on, as), 'Only a person changes how they confirm actions.')
    if (!outcome.ok) return outcome
    if (epoch !== this.epoch || !sameIdentity(outcome.made, this.state.me)) return { ok: false, problem: 'identity_changed', message: 'The identity changed; try again.' }
    this.setState({ me: outcome.made })
    return outcome
  }

  /** Save the person's names or workspace default; these settings need the server. */
  async setProfile(change: Parameters<Api['setProfile']>[0]): Promise<{ ok: true } | { ok: false; message: string }> {
    const epoch = this.epoch
    if (this.state.me?.actor.kind !== 'person') return { ok: false, message: 'Only a person can change these settings.' }
    try {
      const me = await this.api.setProfile(change, this.actingAs())
      if (epoch !== this.epoch || !sameIdentity(me, this.state.me)) return { ok: false, message: 'The identity changed; try again.' }
      this.setState({ me })
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /**
   * Read who is signed in again, since another tab may have signed out, or
   * in as someone else, or a 401 or a close by 4401 says the session may
   * have changed. Each call asks anew, and replaces a read on its way; a 401
   * asks only when none is (`sessionMayHaveChanged`). Someone else: forget
   * the board and load theirs. No one:
   * show sign-in. The same person in the same workspace: nothing is
   * forgotten, and the feed goes on from the cursor; a board whose snapshot
   * was refused takes it again. A read that fails otherwise: nothing
   * changes; but a store that does not know who is signed in yet, whose own
   * read this one replaced (`load`), says why it failed, as that read would
   * have.
   */
  async refreshSession(): Promise<void> {
    if (this.byToken) return
    const read = ++this.sessionRead
    try {
      await this.decideSession(read)
    } finally {
      this.sessionDecided(read)
    }
  }

  /** Read number `read` of who is signed in, and what follows from it (`refreshSession`). */
  private async decideSession(read: number): Promise<void> {
    const epoch = this.epoch
    let me: Me
    try {
      me = await this.readMe(read)
    } catch (err) {
      if (epoch !== this.epoch || read !== this.sessionRead || this.state.status === 'signed_out') return
      if (!this.signedOutBy(err)) {
        if (isOffline(err)) this.setState({ offline: true })
        if (!this.state.me && (!isOffline(err) || !await this.restoreOffline(epoch, read))) this.showError(err)
      }
      return
    }
    if (epoch !== this.epoch || read !== this.sessionRead) return
    this.sessionState('confirmed')
    if (sameIdentity(me, this.state.me)) {
      const wasOffline = this.state.offline
      this.setState({ me })
      await this.connectLocal(me, epoch, read)
      if (epoch !== this.epoch || read !== this.sessionRead) return
      if (this.state.status !== 'ready' && this.snapshots === 0) {
        await this.takeSnapshot()
        this.schedule()
      } else if (wasOffline) {
        await this.readFeed(false)
      } else {
        await this.drain()
      }
      return
    }
    this.forget({ me })
    const newEpoch = this.epoch
    await this.connectLocal(me, newEpoch, read)
    if (newEpoch !== this.epoch || read !== this.sessionRead) return
    await this.takeSnapshot()
    this.schedule()
  }

  /**
   * Once the last read of who is signed in that was asked for has decided:
   * it joins a read on its way, or asks, as a 401 does
   * (`sessionMayHaveChanged`). A newer read, asked for meanwhile, is waited
   * for too: an older one that lands first decides nothing. What follows a
   * 401 of the pages' own, as a session's transcript's, waits for it before
   * it reads who the store acts as. With a token, at once.
   */
  whenSessionKnown(): Promise<void> {
    if (this.byToken) return Promise.resolve()
    this.sessionMayHaveChanged()
    return this.waitForSessionDecision()
  }

  /** Wait without starting another read; startup has already asked. */
  private waitForSessionDecision(): Promise<void> {
    if (this.sessionDecidedRead >= this.sessionRead) return Promise.resolve()
    return new Promise((resolve) => this.sessionWaiters.push(resolve))
  }

  /** Read number `read` of who is signed in has decided: once the last one asked for has, those who wait for it go on. */
  private sessionDecided(read: number): void {
    this.sessionDecidedRead = Math.max(this.sessionDecidedRead, read)
    if (this.sessionDecidedRead < this.sessionRead) return
    const waiters = this.sessionWaiters
    this.sessionWaiters = []
    for (const resolve of waiters) resolve()
  }

  /**
   * Read the feed from the cursor until it is caught up, or take a snapshot
   * when the feed says so. A failed read leaves the cursor where it was.
   */
  catchUp(): Promise<void> {
    this.catching ??= this.readFeed().finally(() => {
      this.catching = null
    })
    return this.catching
  }

  private async readFeed(verifyIdentity = true): Promise<void> {
    if (verifyIdentity && this.state.offline && !this.byToken) {
      await this.refreshSession()
      return
    }
    for (;;) {
      const generation = this.generation
      let page
      try {
        page = await this.api.changes(this.cursor, this.pageSize, this.actingAs(), this.feedEpoch)
      } catch (err) {
        if (generation !== this.generation || this.unauthorized(err)) return
        if (err instanceof ApiError && err.code === SNAPSHOT_NEEDED) await this.takeSnapshot(true)
        else if (isOffline(err)) this.setState({ offline: true })
        // Otherwise the next poll tries again from the same cursor.
        return
      }
      if (generation !== this.generation) return
      this.applyChanges(page.changes, page.cursor)
      if (this.state.offline) this.setState({ offline: false })
      if (page.changes.length < this.pageSize) {
        for (const [id, error] of this.detailErrors) {
          if (this.watched.has(id) && (error.status === 0 || error.status >= 500)) void this.readPlan(id)
        }
        await this.drain()
        return
      }
    }
  }

  /**
   * Apply changes from the feed, in seq order; those at or below the cursor
   * were applied already. `cursor` moves the cursor past them.
   */
  applyChanges(changes: readonly Change[], cursor?: number): void {
    let changed = false
    const confirmed: string[] = []
    for (const change of [...changes].sort((a, b) => a.seq - b.seq)) {
      if (this.outbox.some((p) => p.mutationId === change.mutation_id)) confirmed.push(change.mutation_id)
      changed = this.settle(change.mutation_id) || changed
      if (change.seq <= this.cursor) continue
      this.cursor = change.seq
      changed = this.absorb(change.seq, change) || changed
    }
    if (cursor !== undefined && cursor > this.cursor) this.cursor = cursor
    if (changed) this.setState(this.visible())
    if (confirmed.length) {
      const local = this.local
      const epoch = this.epoch
      const finish = () => {
        if (epoch !== this.epoch) return
        this.outbox = this.outbox.filter((p) => !confirmed.includes(p.mutationId))
        for (const id of confirmed) if (id !== this.sendingId) this.complete(id, true)
        this.setState(this.visible())
      }
      if (local) {
        const recorded = local.finish(confirmed, this.baseForDisk()).then(finish)
        for (const id of confirmed) this.confirmations.set(id, recorded)
        void recorded.catch((err: unknown) => {
          if (epoch === this.epoch) this.onFailure(errorMessage(err))
        }).finally(() => {
          if (epoch === this.epoch) for (const id of confirmed) this.confirmations.delete(id)
        })
      } else finish()
    } else this.persistSoon()
  }

  /** Keep an entity as a change at `seq` left it, unless the store holds a newer one; true when it did. */
  private absorb(seq: number, { entity, data }: Entity): boolean {
    const known = this.rowSeq.get(data.id) ?? this.snapshotSeq
    if (seq <= known) return false
    switch (entity) {
      case 'attachment':
        this.attachmentBase.set(data.id, data)
        break
      case 'project':
        this.projectBase.set(data.id, data)
        break
      case 'label':
        if (data.deleted_at === null) this.labelBase.set(data.id, data)
        else {
          this.labelBase.delete(data.id)
          for (const [id, issue] of this.issueBase) {
            if (issue.labels.includes(data.id)) this.issueBase.set(id, { ...issue, labels: issue.labels.filter(label => label !== data.id) })
          }
        }
        break
      case 'issue':
        this.issueBase.set(data.id, data)
        break
      case 'record_entry':
        this.entryBase.set(data.id, data)
        break
      case 'question':
        this.settledBase.delete(data.id)
        if (inPlay(data)) this.questionBase.set(data.id, data)
        else {
          this.questionBase.delete(data.id)
          this.keepSettled(data)
        }
        break
      case 'session':
        this.sessionBase.set(data.id, data)
        break
      case 'run':
        this.runBase.set(data.id, data)
        break
      case 'runner':
        this.runnerBase.set(data.id, data)
        break
      case 'job':
        if (jobOpen(data)) this.jobBase.set(data.id, data)
        else this.jobBase.delete(data.id)
        break
      case 'proposal':
        if (data.state === 'open') this.proposalBase.set(data.id, data)
        else this.proposalBase.delete(data.id)
        if (data.state === 'returned') this.returnedBase.set(data.id, data)
        else this.returnedBase.delete(data.id)
        // A returned proposal is its issue's newest only until another follows it.
        for (const [id, other] of this.returnedBase) {
          if (other.issue_id === data.issue_id && id !== data.id && other.created_at <= data.created_at) this.returnedBase.delete(id)
        }
        break
      case 'landing':
        // A finished one is kept aside: the proposals it holds may come after it.
        if (data.state !== 'finished') this.landingBase.set(data.id, data)
        else {
          this.landingBase.delete(data.id)
          if (data.outcome?.outcome !== 'landed') this.heldBase.set(data.id, data)
        }
        break
      case 'plan':
        if (data.archived_at === null) this.planBase.set(data.id, data)
        else this.planBase.delete(data.id)
        this.planMoved(data.id, data.revision)
        break
      case 'plan_comment': {
        // The snapshot carries no comment: one joins the plan read, if a page reads it.
        this.planReads.get(data.plan)?.comments.set(data.id, data)
        const detail = this.detailBase.get(data.plan)
        if (detail) {
          this.detailBase.set(data.plan, { ...detail, comments: upsert(detail.comments ?? [], data) })
          this.keepDetail(data.plan)
        }
        break
      }
      default:
        // A kind of entity these pages do not show yet.
        return false
    }
    this.rowSeq.set(data.id, seq)
    return true
  }

  /** Keep the last row of a question that left play (`BoardState.settled`): for the screens open now, not as history, so the oldest go first. */
  private keepSettled(question: Question): void {
    this.settledBase.delete(question.id)
    this.settledBase.set(question.id, question)
    for (const id of this.settledBase.keys()) {
      if (this.settledBase.size <= SETTLED_KEPT) break
      this.settledBase.delete(id)
    }
  }

  /**
   * Hold a snapshot's questions in play. One held before that the snapshot
   * no longer has left play unseen: it settles as last seen, but answered,
   * it settles delivered, since an answered question leaves play only once
   * its delivery is confirmed (`inPlay`). One back in play is no longer
   * settled.
   */
  private replaceQuestions(rows: readonly Question[]): void {
    const next = new Map(rows.map((q) => [q.id, q]))
    for (const [id, question] of this.questionBase) {
      if (!next.has(id)) this.keepSettled(question.state === 'answered' ? { ...question, delivery: 'confirmed' } : question)
    }
    for (const id of next.keys()) this.settledBase.delete(id)
    this.questionBase = next
  }

  /** Keep what a mutation answered; true when it changed what the store holds. */
  private absorbAnswer({ seq, result }: MutationResult): boolean {
    const entity = entityOf(result)
    const changed = entity !== null && this.absorb(seq, entity)
    if (changed) this.persistSoon()
    return changed
  }

  // --- changes the person makes --------------------------------------------------

  /**
   * Make a project (`create_project`). Nothing shows before the server makes
   * it; then the board shows it, selected. A refusal answers the server's words.
   */
  async createProject(key: string, name: string): Promise<ProjectOutcome> {
    const epoch = this.epoch
    const id = this.newId()
    try {
      const mutation: Mutation = { mutation_id: this.newMutationId(), op: 'create_project', args: { id, key: key.trim(), name: name.trim() } }
      const answer = await this.api.mutate(mutation, this.actingAs())
      if (epoch !== this.epoch) return { ok: true }
      this.absorbAnswer(answer)
      this.setState({ ...this.visible(), selectedProjectId: id })
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /** Set the project's repository and branch; keep the server's project and answer a refusal in its words. */
  async setProjectRepository(projectId: string, owner: string, name: string, branch: string): Promise<ProjectOutcome> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'set_project_repository', args: { project_id: projectId, owner: owner.trim(), name: name.trim(), ...(branch.trim() ? { branch: branch.trim() } : {}) } }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /** Set the agent the project's PM agent runs as (`set_pm_agent`); keep the server's project and answer a refusal in its words. */
  async setPmAgent(projectId: string, pm: PmAgent): Promise<ProjectOutcome> {
    const epoch = this.epoch
    try {
      const args = { project_id: projectId, agent: pm.agent, ...(pm.model ? { model: pm.model } : {}), ...(pm.runner_id ? { runner_id: pm.runner_id } : {}) }
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'set_pm_agent', args }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  async setProjectPolicy(projectId: string, policy: LandingPolicy): Promise<ProjectOutcome> {
    const epoch = this.epoch
    const outcome = await this.confirmedAction(
      () => policyAction(projectId, policy),
      as => this.api.mutate({ mutation_id: this.newMutationId(), op: 'set_project_policy', args: { project_id: projectId, policy } }, as),
      "Only a person sets a project's landing policy.",
    )
    if (!outcome.ok) return { ok: false, message: outcome.message }
    if (epoch === this.epoch && this.absorbAnswer(outcome.made)) this.setState(this.visible())
    return { ok: true }
  }

  async setReleaseMap(projectId: string, map: ReleaseMap | null): Promise<ProjectOutcome> {
    // As the other project settings do, keep the server's project whole.
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'set_release_map', args: { project_id: projectId, map } }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /**
   * Set the limits a worker started in the project takes (`set_worker_limits`,
   * Poulpe-486), or none for Poulpe's own. `expected` is what the person read:
   * the server refuses the save when another changed them since.
   */
  async setWorkerLimits(projectId: string, expected: WorkerLimits | null, limits: WorkerLimits | null): Promise<ProjectOutcome> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'set_worker_limits', args: { project_id: projectId, expected, limits } }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /**
   * Set the project's columns of its own (`set_project_columns`), the whole
   * list in the order the board shows them; keep the server's project and
   * answer a refusal in its words.
   */
  async setProjectColumns(projectId: string, columns: SetProjectColumnsArgs['columns']): Promise<ProjectOutcome> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'set_project_columns', args: { project_id: projectId, columns } }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /**
   * Make an issue in the project, in Backlog (`create_issue`). It shows at
   * once, numbered next in its project as far as the store knows; the
   * server's row replaces it. The issue the server made, once it has; null
   * otherwise: when it refused, the issue is gone and `onFailure` was told.
   */
  async createIssue(projectId: string, title: string): Promise<Issue | null> {
    const project = this.projectBase.get(projectId)
    if (!project) return null
    const id = this.newId()
    const numbers = [...this.workingIssues().values()].filter((i) => i.project === projectId).map((i) => i.number)
    const number = Math.max(0, ...numbers) + 1
    const now = new Date().toISOString()
    const created: Issue = {
      id,
      project: projectId,
      number,
      key: issueKey(project.key, number),
      title: title.trim(),
      state: 'backlog',
      priority: 'none',
      description: '',
      summary: '',
      created_by: this.state.me?.actor ?? null,
      plan: { text: '', approval: null },
      outcome: '',
      labels: [],
      claim: null,
      column: null,
      sort_key: this.topOf(projectId, 'backlog', id),
      created_at: now,
      state_changed_at: now,
      updated_at: now,
      started_at: null,
      completed_at: null,
      cancelled_at: null,
      over_budget: [],
      relations: [],
      parent: null,
    }
    const op: Op = { op: 'create_issue', args: { id, project_id: projectId, title: created.title } }
    const made = await this.send({ issueId: id, patch: {}, created }, op, (err) => `Could not create the issue: ${errorMessage(err)}`)
    return made ? (this.state.issues.get(id) ?? created) : null
  }

  /** Change the title, the priority or the description (`update_issue`). */
  updateIssue(id: string, fields: IssueFields): Promise<boolean> {
    return this.change(id, fields, { op: 'update_issue', args: { id, ...fields } })
  }

  /** Append through the outbox; with `parent`, a discussion replying to that comment. True once kept on the device, without waiting for the server. */
  addRecord(issueId: string, kind: EntryKind, body: string, id = this.newId(), parent?: string): Promise<boolean> {
    const issue = this.workingIssues().get(issueId)
    const author = this.state.me?.actor
    const text = body.trim()
    if (!issue || !author || !text) return Promise.resolve(false)
    const refs = parent ? [{ kind: 'entry' as const, id: parent }] : undefined
    const record: RecordEntry = {
      id, issue: issueId, number: 0, kind, body: text, author, at: new Date().toISOString(), ...(refs ? { refs } : {}),
    }
    return this.send({ issueId, patch: {}, record }, {
      op: 'add_record', args: { id, issue_id: issueId, kind, body: text, ...(refs ? { refs } : {}) },
    }, (err) => failureMessage(issue.key, {}, err))
  }

  /**
   * Move the issue to another state, or into a column of its project's own
   * (`column`, whose state is `state`), or to another place in its column
   * (`move_issue`), as the server's rules allow the person: to `sortKey`
   * when given; otherwise to the top of the new column when the column
   * changes, as the server puts it, and where it is when it does not.
   */
  moveIssue(id: string, state: StateSlug, sortKey?: string, column: string | null = null): Promise<boolean> {
    const issue = this.workingIssues().get(id)
    const moved = !issue || issue.state !== state || (issue.column ?? null) !== column
    const place = sortKey ?? (issue && moved ? this.topOf(issue.project, column ?? state, id) : undefined)
    const patch: IssuePatch = { ...(moved ? { state, column } : {}), ...(place === undefined ? {} : { sort_key: place }) }
    const args: MoveIssueArgs = { id, state, ...(column ? { column } : {}), ...(sortKey === undefined ? {} : { sort_key: sortKey }) }
    return this.change(id, patch, { op: 'move_issue', args })
  }

  /**
   * The key of an issue put at the top of `project`'s column `place` (a
   * state, or the id of a column of its own), as the store shows the column,
   * without issue `id`. An issue from a server from before the manual order
   * has no key, and does not count.
   */
  private topOf(project: string, place: string, id: string): string {
    const keys = [...this.workingIssues().values()].flatMap((i) => (i.project === project && (i.column ?? i.state) === place && i.id !== id && typeof i.sort_key === 'string' ? [i.sort_key] : []))
    return topKey(firstKey(keys))
  }

  /** Put one of its project's labels on the issue (`add_label`): its labels stay in the order of their ids, as the server keeps them. */
  addLabel(id: string, labelId: string): Promise<boolean> {
    const labels = this.workingIssues().get(id)?.labels ?? []
    const next = labels.includes(labelId) ? [...labels] : [...labels, labelId].sort()
    return this.change(id, { labels: next }, { op: 'add_label', args: { id, label_id: labelId } })
  }

  /** Take a label off the issue (`remove_label`). */
  removeLabel(id: string, labelId: string): Promise<boolean> {
    const labels = this.workingIssues().get(id)?.labels ?? []
    return this.change(id, { labels: labels.filter((l) => l !== labelId) }, { op: 'remove_label', args: { id, label_id: labelId } })
  }

  /**
   * Relate the issue to another, as `kind` says from its side
   * (`relate_issues`), through the outbox: the relation shows on the issue
   * at once, and on the other once the feed carries it. Relating again
   * changes nothing.
   */
  relateIssues(id: string, kind: RelationKind, other: string): Promise<boolean> {
    const issues = this.workingIssues()
    const key = issues.get(other)?.key
    if (!key || other === id) return Promise.resolve(false)
    const relations = issues.get(id)?.relations ?? []
    const there = relations.some((r) => r.kind === kind && r.issue === other)
    const next = there ? [...relations] : sortRelations([...relations, { kind, issue: other, key }])
    return this.change(id, { relations: next }, { op: 'relate_issues', args: { issue: id, kind, other } })
  }

  /** Undo one of the issue's relations (`unrelate_issues`), through the outbox, as `relateIssues` makes one. */
  unrelateIssues(id: string, kind: RelationKind, other: string): Promise<boolean> {
    const relations = this.workingIssues().get(id)?.relations ?? []
    const next = relations.filter((r) => !(r.kind === kind && r.issue === other))
    return this.change(id, { relations: next }, { op: 'unrelate_issues', args: { issue: id, kind, other } })
  }

  /**
   * Make the issue a sub-issue of `parent`, or none when it is null
   * (`set_parent`), through the outbox: the parent shows at once, and a
   * refusal (another project, a loop, too deep) rolls it back.
   */
  setParent(id: string, parent: string | null): Promise<boolean> {
    if (parent === id) return Promise.resolve(false)
    return this.change(id, { parent }, { op: 'set_parent', args: { issue: id, parent } })
  }

  /** Claim the issue for the person the store acts as (`claim_issue`): another's claim is refused, and the claim rolls back. */
  claimIssue(id: string): Promise<boolean> {
    const by = this.state.me?.actor
    return this.change(id, by ? { claim: { by, at: new Date().toISOString() } } : {}, { op: 'claim_issue', args: { id } })
  }

  /** Release the issue's claim, whoever holds it (`release_issue`). */
  releaseIssue(id: string): Promise<boolean> {
    return this.change(id, { claim: null }, { op: 'release_issue', args: { id } })
  }

  /**
   * Make a label of the project (`create_label`). Nothing shows before the
   * server makes it, since its name may be refused.
   */
  createLabel(projectId: string, name: string, colour: string): Promise<LabelOutcome> {
    return this.labelOp({ op: 'create_label', args: { id: this.newId(), project_id: projectId, name: name.trim(), colour } })
  }

  /** Rename or recolour a label through the durable outbox. */
  updateLabel(id: string, fields: Pick<UpdateLabelArgs, 'name' | 'colour'>): Promise<LabelOutcome> {
    const label = this.workingLabels().get(id)
    if (!label) return Promise.resolve({ ok: false, message: 'The label is no longer available.' })
    const patch = { ...fields, ...(fields.name === undefined ? {} : { name: fields.name.trim() }) }
    return this.queueLabel({ ...label, ...patch }, { op: 'update_label', args: { id, ...patch } })
  }

  /** Delete through the outbox, removing the label from its issues at once. */
  deleteLabel(id: string): Promise<LabelOutcome> {
    const label = this.workingLabels().get(id)
    if (!label) return Promise.resolve({ ok: false, message: 'The label is no longer available.' })
    return this.queueLabel({ ...label, deleted_at: new Date().toISOString() }, { op: 'delete_label', args: { id } })
  }

  private async queueLabel(label: Label, op: Op): Promise<LabelOutcome> {
    const mutationId = this.newMutationId()
    const entry: OutboxEntry = { mutationId, mutation: { ...op, mutation_id: mutationId }, issueId: '', issueKey: label.name, patch: {}, label }
    let message = 'The label could not be saved.'
    const ok = await this.enqueue(entry, this.epoch, err => { message = errorMessage(err); return message })
    return ok ? { ok: true, label: this.state.labels.get(label.id) ?? label } : { ok: false, message }
  }

  private workingLabels(): Map<string, Label> {
    const labels = new Map(this.labelBase)
    for (const { label } of this.outbox) {
      if (label) { if (label.deleted_at) labels.delete(label.id); else labels.set(label.id, label) }
    }
    return labels
  }

  /** Attach once the server confirms it. Files are deliberately not queued offline. */
  async attachFile(args: AttachFileArgs): Promise<{ ok: true } | { ok: false; message: string }> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'attach_file', args }, this.actingAs())
      if (epoch !== this.epoch) return { ok: false, message: 'The identity changed; try again.' }
      if (!('attachment' in answer.result)) return { ok: false, message: 'The attachment could not be read.' }
      if (this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /**
   * A file of the board. The server's answer is kept for this person's
   * device, once the identity is known to be the same, and read back only
   * when the server cannot be reached: a refusal or an error is never
   * answered from the kept copy, and a file the server no longer gives
   * (403, 404) is dropped from it.
   */
  async attachmentFile(id: string): Promise<Blob> {
    const epoch = this.epoch
    let file: Blob
    try {
      file = await this.api.attachmentFile(id, this.actingAs())
    } catch (err) {
      if (epoch !== this.epoch) throw err
      const local = this.local
      if (local && err instanceof ApiError && (err.status === 403 || err.status === 404)) void local.dropFile(id).catch(() => {})
      else if (local && isOffline(err)) {
        const kept = await local.readFile(id).catch(() => null)
        if (kept && epoch === this.epoch) return kept
      }
      throw err
    }
    if (epoch !== this.epoch) throw new Error('The identity changed; try again.')
    void this.local?.keepFile(id, file).catch(() => {})
    return file
  }

  async promptFile(id: string): Promise<Blob> {
    const epoch = this.epoch
    const file = await this.api.promptFile(id, this.actingAs())
    if (epoch !== this.epoch) throw new Error('The identity changed; try again.')
    return file
  }

  /** Send an op on a label, and keep the label it answers. A refusal answers the server's words. */
  private async labelOp(op: Op): Promise<LabelOutcome> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ ...op, mutation_id: this.newMutationId() }, this.actingAs())
      if (!('label' in answer.result)) return { ok: false, message: 'The board server sent a response that could not be read.' }
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true, label: answer.result.label }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /**
   * Save one of the three parts, each with its op, from `base`, the text the
   * edit started from: the server merges it with the changes made since, and
   * the row it answers shows the merged text. Edits it cannot merge are
   * refused, and roll back as any refusal. A plan that changes loses its
   * approval, as on the server.
   */
  savePart(id: string, part: Part, text: string, base?: string): Promise<boolean> {
    switch (part) {
      case 'description':
        return this.change(id, { description: text }, { op: 'update_issue', args: { id, description: text, description_base: base } })
      case 'plan': {
        const plan = this.workingIssues().get(id)?.plan
        return this.change(id, { plan: plan?.text === text ? plan : { text, approval: null } }, { op: 'set_plan', args: { id, plan: text, base } })
      }
      case 'outcome':
        return this.change(id, { outcome: text }, { op: 'set_outcome', args: { id, outcome: text, base } })
    }
  }

  /**
   * Show `patch` on the issue at once and send `op`. True once the server
   * confirms it; false when it refused it, after it is rolled back and
   * `onFailure` has been told.
   */
  private change(id: string, patch: IssuePatch, op: Op): Promise<boolean> {
    const issue = this.workingIssues().get(id)
    if (!issue) return Promise.resolve(false)
    return this.send({ issueId: id, patch }, op, (err) => failureMessage(issue.key, patch, err))
  }

  /** Show a pending change at once and send `op`, as `change` says; `failure` words a refusal. */
  private async send(change: Omit<Pending, 'mutationId' | 'mutation'>, op: Op, failure: (err: unknown) => string): Promise<boolean> {
    const mutationId = this.newMutationId()
    const epoch = this.epoch
    const entry: OutboxEntry = { mutationId, mutation: { ...op, mutation_id: mutationId }, ...change, issueKey: this.state.issues.get(change.issueId)?.key ?? change.created?.key ?? change.issueId }
    return this.enqueue(entry, epoch, failure)
  }

  private async enqueue(entry: OutboxEntry, epoch: number, failure: (err: unknown) => string, refusedId?: string): Promise<boolean> {
    const { mutationId } = entry
    const local = this.local
    if (this.useLocal && !local && (!this.localUnavailable || this.state.offline)) {
      this.onFailure('The change could not be saved on this device.')
      return false
    }
    const outcome = new Promise<boolean>((resolve) => this.outcomes.set(mutationId, resolve))
    this.failures.set(mutationId, failure)
    this.outbox.push(entry)
    const write = local?.append(entry, this.baseForDisk(), refusedId)
    if (write) this.durable.set(mutationId, write)
    // Shown at once, before this device has kept it (Poulpe-710): nothing is
    // sent before the write ends (`drain` waits for it), so a crash before
    // then leaves the disk as it was, and the server's rows win on reload.
    this.pending.push(entry)
    const retried = refusedId ? this.state.refused.find((r) => r.mutationId === refusedId) : undefined
    if (retried) this.setState({ refused: this.state.refused.filter((r) => r !== retried) })
    this.setState(this.visible())
    try {
      if (write) await write
      if (epoch !== this.epoch) return false
    } catch (err) {
      if (epoch !== this.epoch) return false
      this.outbox = this.outbox.filter((p) => p.mutationId !== mutationId)
      this.settle(mutationId)
      // The write that failed kept the refused change on disk: it stays listed.
      this.setState({ ...(retried ? { refused: [...this.state.refused, retried] } : {}), ...this.visible() })
      this.complete(mutationId, false)
      this.onFailure(failure(err))
      return false
    }
    // A comment's editor may clear as soon as its outbox entry is kept.
    // Memory-only fixtures keep it in the outbox too; a storage fallback
    // must still wait for the server, since this device could keep nothing.
    if (this.state.offline || ((entry.record || entry.comment) && (local || !this.useLocal))) this.complete(mutationId, true)
    if (!this.state.offline) void this.drain()
    return outcome
  }

  private complete(id: string, ok: boolean): void {
    this.outcomes.get(id)?.(ok)
    this.outcomes.delete(id)
    this.failures.delete(id)
    this.durable.delete(id)
  }

  /** Keep trying until pending changes settle, even if a recovery read fails. */
  private scheduleRetry(): void {
    if (!this.running || !this.outbox.length || this.retryTimer !== null) return
    const epoch = this.epoch
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null
      if (epoch !== this.epoch || !this.running || !this.outbox.length) return
      const again = () => {
        if (epoch === this.epoch) this.scheduleRetry()
      }
      void this.catchUp().then(again, (err: unknown) => {
        if (epoch !== this.epoch) return
        this.onFailure(errorMessage(err))
        again()
      })
    }, this.pollMs > 0 ? this.pollMs : 2_000)
  }

  /** One request at a time, in durable insertion order, after catching up. */
  private drain(): Promise<void> {
    if (!this.running || this.draining || this.state.offline) return Promise.resolve()
    const epoch = this.epoch
    const local = this.local
    const as = this.actingAs()
    let blocked = false
    const work = async () => {
      while (epoch === this.epoch && this.running) {
        const entry = this.outbox[0]
        if (!entry) return
        try {
          const durable = this.durable.get(entry.mutationId)
          if (durable) await durable
          if (epoch !== this.epoch || !this.running) return
          this.sendingId = entry.mutationId
          const answer = await this.api.mutate(entry.mutation, as)
          if (epoch !== this.epoch) return
          this.bodySaved(entry, answer)
          this.absorbAnswer(answer)
          // Keep the feed cursor: an answer may skip changes to other rows.
          if (local) await local.finish([entry.mutationId], this.baseForDisk())
          if (epoch !== this.epoch) return
          this.settle(entry.mutationId)
          this.outbox = this.outbox.filter((p) => p.mutationId !== entry.mutationId)
          this.complete(entry.mutationId, true)
          this.setState(this.visible())
        } catch (err) {
          if (epoch !== this.epoch) return
          // The feed may have confirmed it while its HTTP answer was lost.
          const confirmed = this.confirmations.get(entry.mutationId)
          if (confirmed) {
            try { await confirmed } catch { blocked = true; return }
            this.complete(entry.mutationId, true)
            continue
          }
          if (!this.outbox.some((p) => p.mutationId === entry.mutationId)) {
            this.complete(entry.mutationId, true)
            continue
          }
          if (err instanceof ApiError && err.status >= 400 && err.status < 500 && err.status !== 401) {
            const refused: RefusedChange = { ...entry, message: err.message, reason: err.reason, detail: err.detail }
            try {
              if (local) await local.finish([entry.mutationId], this.baseForDisk(), refused)
            } catch (storageError) {
              blocked = true
              this.onFailure(errorMessage(storageError))
              for (const pending of this.outbox) this.complete(pending.mutationId, true)
              return
            }
            if (epoch !== this.epoch) return
            const message = this.failures.get(entry.mutationId)?.(err) ?? failureMessage(entry.issueKey, entry.patch, err)
            this.settle(entry.mutationId)
            this.outbox = this.outbox.filter((p) => p.mutationId !== entry.mutationId)
            this.setState({ refused: [...this.state.refused, refused], ...this.visible() })
            this.onFailure(message)
            this.complete(entry.mutationId, false)
            continue
          }
          if (isOffline(err)) this.setState({ offline: true })
          blocked = true
          // A session's 401 keeps the change, as no answer does: who is signed in
          // decides what follows. A bearer token has no session to read again,
          // so its 401 is an error, said and resolved false.
          if (!this.unauthorized(err) && err instanceof ApiError && err.status === 401) {
            const message = this.failures.get(entry.mutationId)?.(err)
            if (message) this.onFailure(message)
            this.complete(entry.mutationId, false)
          }
          // Memory mode cannot acknowledge a change the server did not take.
          if (this.useLocal && !local) {
            for (const pending of this.outbox) {
              this.settle(pending.mutationId)
              this.complete(pending.mutationId, false)
            }
            this.outbox = []
            this.setState(this.visible())
            this.onFailure('The change could not be saved on this device.')
            return
          }
          // Saved locally: callers can close their editor even though replay waits.
          for (const pending of this.outbox) this.complete(pending.mutationId, true)
          return
        }
      }
    }
    // Reserve the drain before executing it; in-memory fixtures still send synchronously.
    this.draining = Promise.resolve()
    const running = work().finally(() => {
      if (this.draining === running) {
        this.draining = null
        this.sendingId = null
        if (!blocked && this.outbox.length) void this.drain()
        if (blocked) this.scheduleRetry()
      }
    })
    this.draining = running
    return running
  }

  /**
   * Send a refused change again, with a new mutation id and the same args. A
   * `text_conflict` keeps the base its edit started from, so the server
   * merges it again with the text as it then stands, and refuses it again,
   * with both texts, while the edits still overlap. Sending the current text
   * as the base would store the person's text over the other edit.
   */
  async retryRefused(id: string): Promise<boolean> {
    const refused = this.state.refused.find((r) => r.mutationId === id)
    if (!refused) return false
    const mutationId = this.newMutationId()
    const { message: _message, reason: _reason, detail: _detail, ...entry } = refused
    return this.enqueue({ ...entry, mutationId, mutation: { ...entry.mutation, mutation_id: mutationId } }, this.epoch,
      (err) => failureMessage(entry.issueKey, entry.patch, err), id)
  }

  async discardRefused(id: string): Promise<void> {
    const epoch = this.epoch
    try {
      await this.local?.discard(id)
      if (epoch === this.epoch) this.setState({ refused: this.state.refused.filter((r) => r.mutationId !== id) })
    } catch (err) {
      if (epoch === this.epoch) this.onFailure(errorMessage(err))
    }
  }

  /**
   * Answer at the shown version. Online, wait for confirmation; offline,
   * keep the answer in the outbox and show its pending effect. Offline with
   * `here`, the answer goes through this Mac's runner instead (Poulpe-830):
   * once the agent has it, the question leaves the page, and the server
   * learns of it from the runner on reconnect.
   * Online, `mutationId`, when given, names the answer: sending it again
   * with the same id, after a failure whose outcome is unknown, is a replay
   * the server answers without answering twice. The caller that names it
   * keeps the answer itself (the phone, phone/answers.tsx): offline, the
   * store sends nothing and queues nothing, and says so (`offline`).
   */
  async answer(question: Question, replies: Reply[], here?: (replies: Reply[]) => Promise<LocalWriteOutcome>, mutationId?: string): Promise<AnswerOutcome> {
    const epoch = this.epoch
    if (this.state.offline && here) {
      const outcome = await here(replies)
      if (!outcome.done) return { ok: false, stale: false, message: outcome.reason ? `this Mac’s runner did not take it: ${outcome.reason}` : 'this Mac’s runner did not answer' }
      if (epoch === this.epoch && this.questionBase.delete(question.id)) this.setState(this.visible())
      return { ok: true }
    }
    if (this.state.offline && mutationId) return { ok: false, stale: false, offline: true, message: 'Needs the server' }
    if (this.state.offline) {
      const me = this.state.me
      if (!me) return { ok: false, stale: false, message: 'Needs the server' }
      const mutationId = this.newMutationId()
      const answered: Question = { ...question, state: 'answered', answer: { by: me.actor, version: question.version, replies, at: new Date().toISOString() } }
      const issueId = question.issue ?? ''
      const ok = await this.enqueue({ mutationId, mutation: { mutation_id: mutationId, op: 'answer', args: { question_id: question.id, version: question.version, replies } },
        issueId, issueKey: this.state.issues.get(issueId)?.key ?? 'Question', patch: {}, answered }, epoch, errorMessage)
      return ok ? { ok: true } : { ok: false, stale: false, message: 'The change could not be saved on this device.' }
    }
    try {
      const answer = await this.api.mutate(
        { mutation_id: mutationId ?? this.newMutationId(), op: 'answer', args: { question_id: question.id, version: question.version, replies } },
        this.actingAs(),
      )
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch && !this.unauthorized(err)) await this.catchUp()
      return { ok: false, stale: err instanceof ApiError && err.reason === STALE, message: errorMessage(err) }
    }
  }

  /**
   * Send an op on an agent's session, as the member the store acts as, with
   * the mutation id the caller chose, so that sending the same again starts
   * nothing twice. The session it answers shows at once. A refusal rejects,
   * in the server's words; after a 401 the store reads who is signed in, as
   * for any request (`unauthorized`).
   */
  async sendSessionOp(mutation: SessionOp & { mutation_id: string }): Promise<Session> {
    return (await this.sessionAnswer(mutation)).session
  }

  /**
   * Send a prompt that waits now, move it, or take it back (`change_queued_prompt`, Poulpe-430). The session it
   * answers shows at once, without the prompt if it left the queue. A prompt taken back comes back whole, with its
   * files, in `unsent`.
   */
  async changeQueuedPrompt(mutation: Extract<SessionOp, { op: 'change_queued_prompt' }> & { mutation_id: string }): Promise<{ session: Session; unsent?: UnsentPrompt }> {
    const { session, unsent } = await this.sessionAnswer(mutation)
    return { session, ...(unsent ? { unsent } : {}) }
  }

  /** Selection acceptance identifies a job, never a confirmed current value. */
  async sendSessionSettings(mutation: Extract<SessionOp, {op: 'set_session_settings'}> & {mutation_id: string}): Promise<{session: Session; job: string}> {
    const answer = await this.sessionAnswer(mutation)
    if (typeof answer.job_id !== 'string' || answer.job_id.length === 0 || answer.job_id.length > 128) throw new ApiError(0, 'bad_response', 'The settings request could not be confirmed.')
    return {session: answer.session, job: answer.job_id}
  }

  private async sessionAnswer(mutation: SessionOp & {mutation_id: string}): Promise<Extract<MutationResult['result'], { session: Session }>> {
    const epoch = this.epoch
    let answer: MutationResult
    try {
      answer = await this.api.mutate(mutation, this.actingAs())
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      throw err
    }
    if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
    if (!('session' in answer.result)) throw new ApiError(0, 'bad_response', 'The board server sent a response that could not be read.')
    return answer.result
  }

  /**
   * Approve exactly the subject a landing asks for (`approve_landing`). The
   * subject's digest is computed here, from the subject shown; when it is
   * not the one the server asks for, nothing is sent. Otherwise the person
   * confirms it with a passkey (`confirm/start`, the browser's prompt,
   * `confirm/finish`), and then the approval is sent with that subject; a
   * failed ceremony sends no approval. A person whose account asks for no
   * confirmation is asked for none, unless the server still asks
   * (`confirmedAction`). The landing it answers shows at once.
   */
  async approveLanding(landingId: string, asked: ApprovalAsked): Promise<ApprovalOutcome> {
    if (confirmsActions(this.state.me) && !this.passkeys.supported()) return { ok: false, problem: 'unsupported', message: 'This browser has no passkeys.' }
    // Who the person approves as is who the page showed when they asked:
    // every step names that member, and stops once the store's identity has
    // changed, so a sign-in elsewhere never carries this approval to another.
    const epoch = this.epoch
    const identityChanged: ApprovalOutcome = { ok: false, problem: 'identity_changed', message: 'The identity changed; review again.' }
    const digest = await subjectDigest(asked.subject)
    if (digest !== asked.digest) {
      return { ok: false, problem: 'mismatch', message: `The subject's digest is ${digest}, and the server asks for ${asked.digest}.` }
    }
    if (epoch !== this.epoch) return identityChanged
    const outcome = await this.confirmedAction(
      () => ({ kind: 'approve_landing', digest }),
      (as) => this.api.mutate({ mutation_id: this.newMutationId(), op: 'approve_landing', args: { landing_id: landingId, subject: asked.subject } }, as),
      'Only a person approves a landing.',
    )
    if (!outcome.ok) return outcome.problem === 'identity_changed' ? identityChanged : outcome
    const answer = outcome.made
    if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
    const { result } = answer
    if (!('accepted' in result)) return { ok: false, problem: 'failed', message: 'The board server sent a response that could not be read.' }
    return { ok: true, accepted: result.accepted, landing: result.landing }
  }

  /**
   * Say an open proposal looks good (`look_at_proposal`), or ask its session
   * for changes with a note (`return_proposal`), which ends it, or withdraw
   * it with a reason (`withdraw_proposal`), which ends it and prompts no
   * session. Each is sent
   * at once, not kept in the outbox: it answers the proposal as it stands
   * now. The proposal it answers shows at once; a refusal answers the
   * server's words.
   */
  lookAtProposal(id: string): Promise<ProjectOutcome> {
    return this.proposalOp({ op: 'look_at_proposal', args: { proposal: id } })
  }

  returnProposal(id: string, note: string): Promise<ProjectOutcome> {
    return this.proposalOp({ op: 'return_proposal', args: { proposal: id, note } })
  }

  withdrawProposal(id: string, reason: string): Promise<ProjectOutcome> {
    return this.proposalOp({ op: 'withdraw_proposal', args: { proposal: id, reason } })
  }

  /**
   * A person's verdict (`review_verdict`, Poulpe-688) while the project's
   * policy has a person review: for the exact head they read, with a note for
   * its session unless it lands. Sent at once, as the ops above are.
   */
  reviewVerdict(id: string, head: string, outcome: ReviewOutcome, note?: string): Promise<ProjectOutcome> {
    return this.proposalOp({ op: 'review_verdict', args: { proposal_id: id, outcome, head, ...(note ? { note } : {}) } })
  }

  /**
   * Mark an open proposal urgent, or normal again, with a reason
   * (`set_proposal_urgency`), from the revision of its urgency the page
   * shows: a change made since is refused, and the page shows the newer one.
   */
  setProposalUrgency(proposal: Proposal, urgency: Urgency, reason: string): Promise<ProjectOutcome> {
    return this.proposalOp({
      op: 'set_proposal_urgency',
      args: { proposal: proposal.id, urgency, reason, expected_revision: proposal.urgency.revision },
    })
  }

  private async proposalOp(op: Extract<Op, { op: 'look_at_proposal' | 'return_proposal' | 'withdraw_proposal' | 'review_verdict' | 'set_proposal_urgency' }>): Promise<ProjectOutcome> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ ...op, mutation_id: this.newMutationId() }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      return { ok: true }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, message: errorMessage(err) }
    }
  }

  /** The finished landings an open proposal is held by. */
  private holding(): Map<string, Landing> {
    const holders = new Set([...this.proposalBase.values()].flatMap((p) => (p.held_by ? [p.held_by] : [])))
    return new Map([...this.heldBase].filter(([id]) => holders.has(id)))
  }

  /**
   * Merge ready proposals together, in the order given (`merge_together`):
   * sent at once, not kept in the outbox. The landing it answers, and its
   * proposals, show at once.
   */
  async mergeTogether(ids: readonly string[]): Promise<MergeOutcome> {
    const epoch = this.epoch
    try {
      const answer = await this.api.mutate({ mutation_id: this.newMutationId(), op: 'merge_together', args: { proposals: [...ids] } }, this.actingAs())
      if (epoch === this.epoch && this.absorbAnswer(answer)) this.setState(this.visible())
      if (!('landing' in answer.result)) return { ok: false, status: 0, reason: undefined, message: 'The board server sent a response that could not be read.' }
      return { ok: true, landing: answer.result.landing }
    } catch (err) {
      if (epoch === this.epoch) this.unauthorized(err)
      return { ok: false, status: err instanceof ApiError ? err.status : 0, reason: err instanceof ApiError ? err.reason : undefined, message: errorMessage(err) }
    }
  }

  /** Stop showing why a batch stopped, on this page; the cards it holds still say it. */
  dismissLanding(id: string): void {
    if (!this.state.dismissed.has(id)) this.setState({ dismissed: new Set([...this.state.dismissed, id]) })
  }

  private settle(mutationId: string): boolean {
    const count = this.pending.length
    this.pending = this.pending.filter((p) => p.mutationId !== mutationId)
    return this.pending.length !== count
  }

  /** Show this project on the board. */
  selectProject(id: string): void {
    if (this.projectBase.has(id) && this.state.selectedProjectId !== id) this.setState({ selectedProjectId: id })
  }

  /** Whether the server has confirmed this issue, through its answer, feed or snapshot. */
  isIssueConfirmed(id: string): boolean {
    return this.issueBase.has(id)
  }

  /** The descriptions an issue replaced, newest first (Poulpe-439). Read when a page asks; nothing is kept. */
  issueRevisions(id: string): Promise<IssueRevisions> {
    return this.api.issueRevisions(id, this.actingAs())
  }

  /** One replaced description, with its text. */
  issueRevision(id: string, number: number): Promise<IssueRevision> {
    return this.api.issueRevision(id, number, this.actingAs())
  }

  isRecordPending(id: string): boolean {
    return !this.entryBase.has(id) && this.pending.some((change) => change.record?.id === id)
  }

  // --- plans ----------------------------------------------------------------------------

  /**
   * A page shows plan `id`: the store reads its body, its revisions and its
   * comments, and reads them again whenever the feed's revision passes the
   * one it read. Call what it answers when the page no longer shows it.
   */
  watchPlan(id: string): () => void {
    const first = !this.watched.has(id)
    this.watched.set(id, (this.watched.get(id) ?? 0) + 1)
    if (first || !this.detailBase.has(id) || this.detailErrors.has(id)) void this.readPlan(id)
    else this.planMoved(id)
    let done = false
    return () => {
      if (done) return
      done = true
      const left = (this.watched.get(id) ?? 1) - 1
      if (left > 0) this.watched.set(id, left)
      else this.watched.delete(id)
    }
  }

  /** The project’s recent outcomes, including history outside the live snapshot. */
  digest(project: string): Promise<WorkDigest> {
    return this.api.digest(project, {}, this.actingAs())
  }

  /** Asks the server for the issues and plans `query` finds, as the member the pages act as. */
  repoDocs(project: string) { return this.api.repoDocs(project, this.actingAs()) }
  repoDoc(project: string, path: string, sha: string) { return this.api.repoDoc(project, path, sha, this.actingAs()) }

  search(query: string, options: SearchOptions): Promise<SearchAnswer> {
    return this.api.search(query, options, this.actingAs())
  }

  /** Read plan `id` again, as a page asks (after a refusal, or to try again after a failure). */
  refreshPlan(id: string): Promise<void> {
    return this.readPlan(id)
  }

  /** The plan's revision moved past the one read: a watched plan is read again. */
  private planMoved(id: string, revision = this.planBase.get(id)?.revision): void {
    const read = this.detailBase.get(id)
    if (!this.watched.has(id) || revision === undefined || (read && read.revision >= revision)) return
    void this.readPlan(id)
  }

  /** One read of a plan at a time; one asked for meanwhile follows it, so the newest revision is read. */
  private async readPlan(id: string): Promise<void> {
    const reading = this.planReads.get(id)
    if (reading) {
      reading.again = true
      return
    }
    const state = { again: false, comments: new Map<string, PlanComment>() }
    this.planReads.set(id, state)
    const epoch = this.epoch
    try {
      do {
        state.again = false
        state.comments.clear()
        let read: PlanDetail
        try {
          read = await this.api.plan(id, this.actingAs())
        } catch (err) {
          if (epoch !== this.epoch) return
          if (!this.unauthorized(err)) {
            this.detailErrors.set(id, asApiError(err))
            this.setState({ ...this.visible(), ...(isOffline(err) ? { offline: true } : {}) })
          }
          return
        }
        if (epoch !== this.epoch) return
        const kept = this.detailBase.get(id)
        // A read that trails one the store holds is dropped.
        if (!kept || read.revision >= kept.revision) {
          // Comments change independently of the body's revision, even during the first read.
          const comments = mergeComments(kept?.comments ?? [], read.comments ?? [])
          this.detailBase.set(id, { ...read, comments: mergeComments(comments, [...state.comments.values()]) })
          this.detailErrors.delete(id)
          this.keepDetail(id)
          this.setState(this.visible())
        }
        const known = this.planBase.get(id)?.revision
        if (known !== undefined && known > read.revision) state.again = true
      } while (state.again && epoch === this.epoch && this.watched.has(id))
    } finally {
      if (this.planReads.get(id) === state) this.planReads.delete(id)
    }
  }

  /** A body the server took as sent is the body read, at its new revision: no read is needed, and the editor's text stays shown. */
  private bodySaved(entry: OutboxEntry, answer: MutationResult): void {
    if (entry.planBody === undefined || !entry.planId || !('plan' in answer.result) || answer.result.merged) return
    const kept = this.detailBase.get(entry.planId)
    const plan = answer.result.plan
    if (kept && plan.revision >= kept.revision) this.detailBase.set(entry.planId, { ...kept, ...plan, body: entry.planBody })
    this.keepDetail(entry.planId)
  }

  /** Save plan `id`'s detail as the store holds it, for a cold start offline; a failure loses only that. */
  private keepDetail(id: string): void {
    const detail = this.detailBase.get(id)
    if (detail && this.local) void this.local.keepPlan(detail).catch(() => {})
  }

  /** Show `change` on plan `id` at once and send `op`, as `change` does for an issue; `action` words a refusal. */
  private planChange(id: string, change: Omit<Pending, 'mutationId' | 'mutation' | 'issueId' | 'patch' | 'planId'>, op: Op, action: string): Promise<boolean> {
    const plan = this.workingPlans().get(id)
    if (!plan) return Promise.resolve(false)
    const mutationId = this.newMutationId()
    const entry: OutboxEntry = { mutationId, mutation: { ...op, mutation_id: mutationId }, issueId: '', issueKey: plan.title, patch: {}, planId: id, ...change }
    return this.enqueue(entry, this.epoch, (err) => `${action}: ${errorMessage(err)}`)
  }

  /** The plans with the changes still on their way, for a second change made in the same turn. */
  private workingPlans(): Map<string, PlanRow> {
    const plans = new Map(this.planBase)
    for (const { planId, planPatch, createdPlan } of this.outbox) {
      if (!planId) continue
      const plan = plans.get(planId) ?? createdPlan
      if (plan) plans.set(planId, { ...plan, ...planPatch })
    }
    for (const [id, read] of this.detailBase) if (!plans.has(id)) plans.set(id, read)
    return plans
  }

  /**
   * Make a plan of the project (`create_plan`), in Backlog and first among
   * its plans, with `body`. It shows at once; the server's row replaces it.
   * The plan the server made, once it has; null when it refused, after
   * `onFailure` was told.
   */
  async createPlan(projectId: string, title: string, body: string): Promise<PlanRow | null> {
    const author = this.state.me?.actor
    if (!this.projectBase.has(projectId) || !author) return null
    const id = this.newId()
    const now = new Date().toISOString()
    const keys = [...this.workingPlans().values()].filter((p) => p.project === projectId).map((p) => p.sort_key)
    const created: PlanRow = {
      id, project: projectId, title: title.trim(), summary: '', status: 'backlog', format: formatOf(body), revision: 1,
      sort_key: topKey(firstKey(keys)), claim: null, legacy_ref: null, created_by: author, created_at: now, updated_at: now, archived_at: null, issues: [],
    }
    const mutationId = this.newMutationId()
    const op: Op = { op: 'create_plan', args: { id, project: projectId, title: created.title, body } }
    const entry: OutboxEntry = { mutationId, mutation: { ...op, mutation_id: mutationId }, issueId: '', issueKey: created.title, patch: {}, planId: id, createdPlan: created }
    const made = await this.enqueue(entry, this.epoch, (err) => `Could not create the plan: ${errorMessage(err)}`)
    return made ? (this.state.plans.get(id) ?? created) : null
  }

  /**
   * Rename a session (`rename_session`): its title shows at once and is the person's, which its agent's later titles leave alone.
   * The server cleans it as it cleans an agent's; a blank one is not sent.
   */
  renameSession(id: string, title: string): Promise<boolean> {
    const text = title.trim()
    const session = this.state.sessions.get(id)
    if (!text || !session) return Promise.resolve(false)
    const mutationId = this.newMutationId()
    const entry: OutboxEntry = { mutationId, mutation: { op: 'rename_session', args: { session_id: id, title: text }, mutation_id: mutationId }, issueId: '', issueKey: session.title ?? id, patch: {}, sessionId: id, sessionTitle: text }
    return this.enqueue(entry, this.epoch, (err) => `Could not rename the session: ${errorMessage(err)}`)
  }

  /** Rename a plan, change its summary or its status (`update_plan`): last write wins. */
  updatePlan(id: string, fields: Pick<UpdatePlanArgs, 'title' | 'summary' | 'status'>): Promise<boolean> {
    const action = fields.status !== undefined ? 'Could not change the plan status' : fields.summary !== undefined ? 'Could not change the summary' : 'Could not rename the plan'
    return this.planChange(id, { planPatch: fields }, { op: 'update_plan', args: { id, ...fields } }, action)
  }

  /**
   * Save a plan's whole body (`set_plan_body`) from `base`, the body the edit
   * started from: the server merges it with the changes made since, or
   * refuses it 409 `text_conflict`, which the store keeps as a refused change
   * with both texts. True once the server took it.
   */
  setPlanBody(id: string, body: string, base: string): Promise<boolean> {
    return this.planChange(id, { planBody: body }, { op: 'set_plan_body', args: { id, body, base } }, 'Could not save the plan')
  }

  /** Patch a plan's body as it stands (`patch_plan`): each anchor must match it exactly once. */
  patchPlan(id: string, patch: PlanPatchOp[]): Promise<boolean> {
    return this.planChange(id, {}, { op: 'patch_plan', args: { id, patch } }, 'Could not save the plan')
  }

  /** List an issue in a plan, under the phase `section` names or a new one (`list_in_plan`). It shows in the plan's issues at once. */
  listInPlan(planId: string, issueId: string, section?: string): Promise<boolean> {
    const issue = this.workingIssues().get(issueId)
    const plan = this.workingPlans().get(planId)
    if (!issue || !plan) return Promise.resolve(false)
    const issues = plan.issues.some((listing) => listing.key === issue.key) ? plan.issues : [...plan.issues, { key: issue.key, section: section?.trim() || null }]
    return this.planChange(planId, { planPatch: { issues } }, { op: 'list_in_plan', args: { plan: planId, issue: issueId, ...(section?.trim() ? { section: section.trim() } : {}) } },
      `Could not add ${issue.key} to ${plan.title}`)
  }

  /** Take an issue out of a plan (`unlist_from_plan`): the items that list it go, mentions stay. It leaves the plan's issues at once. */
  unlistFromPlan(planId: string, issueId: string): Promise<boolean> {
    const issue = this.workingIssues().get(issueId)
    const plan = this.workingPlans().get(planId)
    if (!issue || !plan) return Promise.resolve(false)
    const issues = plan.issues.filter((listing) => listing.key !== issue.key)
    return this.planChange(planId, { planPatch: { issues } }, { op: 'unlist_from_plan', args: { plan: planId, issue: issueId } },
      `Could not remove ${issue.key} from ${plan.title}`)
  }

  /** Move an issue to another phase of a plan that lists it, or a new one (`move_in_plan`). It shows under that phase at once. */
  moveInPlan(planId: string, issueId: string, section: string): Promise<boolean> {
    const issue = this.workingIssues().get(issueId)
    const plan = this.workingPlans().get(planId)
    const phase = section.trim()
    if (!issue || !plan || !phase) return Promise.resolve(false)
    const issues = plan.issues.map((listing) => (listing.key === issue.key ? { ...listing, section: phase } : listing))
    return this.planChange(planId, { planPatch: { issues } }, { op: 'move_in_plan', args: { plan: planId, issue: issueId, section: phase } },
      `Could not move ${issue.key} in ${plan.title}`)
  }

  /** Claim a plan for the person the store acts as (`claim_plan`): another's claim is refused, and the claim rolls back. */
  claimPlan(id: string): Promise<boolean> {
    const by = this.state.me?.actor
    return this.planChange(id, by ? { planPatch: { claim: { by, at: new Date().toISOString() } } } : {}, { op: 'claim_plan', args: { id } }, 'Could not claim the plan')
  }

  /** Release a plan's claim, whoever holds it (`release_plan`). */
  releasePlan(id: string): Promise<boolean> {
    return this.planChange(id, { planPatch: { claim: null } }, { op: 'release_plan', args: { id } }, 'Could not release the plan')
  }

  /** Comment on a plan, or reply to one of its comments (`add_plan_comment`): it shows at once. True once kept on the device. */
  addPlanComment(planId: string, body: string, parent?: string): Promise<boolean> {
    const author = this.state.me?.actor
    const text = body.trim()
    if (!author || !text) return Promise.resolve(false)
    const id = this.newId()
    const comment: PlanComment = { id, plan: planId, parent: parent ?? null, author, source_author: null, body: text, created_at: new Date().toISOString(), edited_at: null }
    return this.planChange(planId, { comment }, { op: 'add_plan_comment', args: { id, plan: planId, body: text, ...(parent ? { parent } : {}) } },
      parent ? 'Could not post the reply' : 'Could not post the comment')
  }

  /** Edit one's own comment on a plan (`edit_plan_comment`): another's is refused, 403 `not_its_author`. */
  editPlanComment(comment: PlanComment, body: string): Promise<boolean> {
    const text = body.trim()
    if (!text) return Promise.resolve(false)
    const edited: PlanComment = { ...comment, body: text, edited_at: new Date().toISOString() }
    return this.planChange(comment.plan, { comment: edited }, { op: 'edit_plan_comment', args: { id: comment.id, body: text } }, 'Could not save the comment')
  }

  // --- the visible state -----------------------------------------------------------

  /** Include writes still committing when deriving a second change made in the same turn. */
  private workingIssues(): Map<string, Issue> {
    const issues = new Map(this.issueBase)
    for (const entry of this.outbox) {
      const issue = issues.get(entry.issueId) ?? entry.created
      if (issue) issues.set(entry.issueId, applyIssuePatch(issue, entry, issues))
    }
    return issues
  }

  private visible(): Omit<BoardState, 'status' | 'error' | 'me' | 'stream' | 'offline' | 'refused' | 'emailLink' | 'dismissed'> {
    const issues = new Map(this.issueBase)
    const entries = new Map(this.entryBase)
    const questions = new Map(this.questionBase)
    const pendingAnswers = new Set<string>()
    for (const { answered } of this.pending) {
      if (answered) { questions.set(answered.id, answered); pendingAnswers.add(answered.id) }
    }
    for (const entry of this.pending) {
      const issue = issues.get(entry.issueId) ?? entry.created
      if (issue) issues.set(entry.issueId, applyIssuePatch(issue, entry, issues))
    }
    for (const { record } of this.pending) {
      if (!record || entries.has(record.id)) continue
      // Pending appends follow all confirmed entries, including ones that
      // arrived while offline. Their authoritative numbers come from the server.
      const number = Math.max(0, ...[...entries.values()].filter((e) => e.issue === record.issue).map((e) => e.number)) + 1
      entries.set(record.id, { ...record, number })
    }
    const labels = new Map(this.labelBase)
    for (const { label } of this.pending) {
      if (!label) continue
      if (label.deleted_at) {
        labels.delete(label.id)
        for (const [id, issue] of issues) {
          if (issue.labels.includes(label.id)) issues.set(id, { ...issue, labels: issue.labels.filter(id => id !== label.id) })
        }
      } else labels.set(label.id, label)
    }
    const plans = new Map(this.planBase)
    for (const { planId, planPatch, createdPlan } of this.pending) {
      if (!planId) continue
      const plan = plans.get(planId) ?? createdPlan
      if (plan) plans.set(planId, { ...plan, ...planPatch })
    }
    const sessions = new Map(this.sessionBase)
    for (const { sessionId, sessionTitle } of this.pending) {
      const session = sessionId ? sessions.get(sessionId) : undefined
      if (sessionId && session && sessionTitle !== undefined) sessions.set(sessionId, { ...session, title: sessionTitle, title_by_person: true })
    }
    const planDetails = new Map<string, PlanDetail>()
    for (const [id, read] of this.detailBase) {
      // The row as the store has it now, its body and revision as read.
      const row = plans.get(id)
      let detail: PlanDetail = row ? { ...read, ...row, revision: read.revision, body: read.body } : read
      for (const { planId, planBody, comment } of this.pending) {
        if (planId !== id) continue
        if (planBody !== undefined) detail = { ...detail, body: planBody }
        if (comment) detail = { ...detail, comments: upsert(detail.comments ?? [], comment) }
      }
      planDetails.set(id, detail)
    }
    const selected = this.state.selectedProjectId
    return {
      plans,
      planDetails,
      planErrors: new Map(this.detailErrors),
      waiting: this.outbox.length,
      projects: new Map(this.projectBase),
      labels,
      issues,
      entries,
      questions,
      settled: new Map(this.settledBase),
      pendingAnswers,
      provisionalClaims: new Set(this.pending.filter((entry) => entry.mutation.op === 'claim_issue').map((entry) => entry.issueId)),
      sessions,
      attachments: new Map(this.attachmentBase),
      runs: new Map(this.runBase),
      runners: new Map(this.runnerBase),
      jobs: new Map(this.jobBase),
      proposals: new Map(this.proposalBase),
      returned: new Map(this.returnedBase),
      landings: new Map(this.landingBase),
      held: this.holding(),
      selectedProjectId: selected !== null && this.projectBase.has(selected) ? selected : (this.projectBase.keys().next().value ?? null),
    }
  }

  private setState(partial: Partial<BoardState>) {
    this.state = { ...this.state, ...partial }
    for (const listener of this.listeners) listener()
  }
}

/** A list of comments with `comment` in its place, or added at its end. */
function upsert(comments: readonly PlanComment[], comment: PlanComment): PlanComment[] {
  const at = comments.findIndex((c) => c.id === comment.id)
  if (at < 0) return [...comments, comment]
  const next = [...comments]
  next[at] = comment
  return next
}

/** A read's comments, retaining feed additions and later edits. */
function mergeComments(kept: readonly PlanComment[], read: readonly PlanComment[]): PlanComment[] {
  const previous = new Map(kept.map((c) => [c.id, c]))
  const ids = new Set(read.map((c) => c.id))
  const comments = read.map((comment) => {
    const held = previous.get(comment.id)
    return held && Date.parse(held.edited_at ?? held.created_at) > Date.parse(comment.edited_at ?? comment.created_at) ? held : comment
  })
  return [...comments, ...kept.filter((c) => !ids.has(c.id))]
}

function asApiError(err: unknown): ApiError {
  return err instanceof ApiError ? err : new ApiError(0, 'unknown', errorMessage(err))
}

// --- what the pages select -----------------------------------------------------------

const byId = (a: { id: string }, b: { id: string }) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0)

/** An issue's record, oldest entry first. */
export function recordOf(entries: ReadonlyMap<string, RecordEntry>, issueId: string): RecordEntry[] {
  return [...entries.values()].filter((e) => e.issue === issueId).sort((a, b) => a.number - b.number)
}

/** An issue's questions in play, oldest first. The server's ids are UUID v7, which sort by when they were made. */
export function questionsOf(questions: ReadonlyMap<string, Question>, issueId: string): Question[] {
  return [...questions.values()].filter((q) => q.issue === issueId).sort(byId)
}

/**
 * The explicit questions put to `member`, the person the pages act as,
 * newest first; none without one. A completed session turn asks nothing,
 * and is not among them (Poulpe-586). The PM's questions are the
 * PM agent's to answer, and what it cannot settle reaches a person as a
 * question put to them (docs/architecture.md, "Needs you and the digest").
 */
export function needsYou(questions: ReadonlyMap<string, Question>, member: string | null | undefined, pendingAnswers?: ReadonlySet<string>): Question[] {
  if (!member) return []
  return [...questions.values()]
    .filter((q) => (q.state === 'open' || pendingAnswers?.has(q.id)) && q.target.kind === 'person' && q.target.member === member)
    .sort((a, b) => byId(b, a))
}

/** A project's queue: its open proposals, oldest first, as the server orders them. */
export function queueOf(proposals: ReadonlyMap<string, Proposal>, projectId: string | null): Proposal[] {
  return [...proposals.values()]
    .filter((p) => p.project_id === projectId)
    .sort((a, b) => (a.created_at < b.created_at ? -1 : a.created_at > b.created_at ? 1 : byId(a, b)))
}

/** A project's running landing, if any: one runs at a time. */
export function landingOf(landings: ReadonlyMap<string, Landing>, projectId: string | null): Landing | null {
  return [...landings.values()].find((l) => l.project_id === projectId && l.state === 'running') ?? null
}

/** An issue's open proposal, the oldest if it has several, among those that carry it (Poulpe-554), and its running landing, if any. */
export function issueProposal(
  proposals: ReadonlyMap<string, Proposal>,
  landings: ReadonlyMap<string, Landing>,
  issueId: string,
): { proposal: Proposal; landing: Landing | null } | null {
  const proposal = [...proposals.values()].filter((p) => p.issue_id === issueId || p.issue_ids.includes(issueId)).sort((a, b) => (a.created_at < b.created_at ? -1 : 1))[0]
  if (!proposal) return null
  return { proposal, landing: [...landings.values()].find((l) => l.proposals.includes(proposal.id)) ?? null }
}
